import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import Keycloak from 'keycloak-js';
import { Subscription } from 'rxjs';
import { catchError, forkJoin, from, interval, of, switchMap, takeWhile } from 'rxjs';

import { SelfServiceConfigService } from '../shared/self-service-config.service';
import { SelfServiceApiConfigService } from '../shared/self-service-api-config.service';
import { BuilderLayoutConfig } from '../shared/self-service-config.types';
import { SelfServiceDatasetService } from '../shared/self-service-dataset.service';
import { SpricedLiteApiService } from './spriced-lite-api.service';

interface Rule {
  ruleId: string;
  outputField: string;
  expression: string;
  description?: string | null;
}

interface ExcelColumn {
  columnLetter: string;
  header: string;
  fieldName: string;
  dataType: string;
  sampleValue: unknown;
}

interface ExcelFormula {
  cell: string;
  outputField: string;
  formula: string;
  expression: string;
  ruleId: string;
}

interface SheetAnalysis {
  sheetName: string;
  columns: ExcelColumn[];
  formulas: ExcelFormula[];
  rules: Rule[];
}

interface ExcelAnalysisResponse {
  fileName: string;
  selectedSheet: string;
  sheets: string[];
  columns: ExcelColumn[];
  formulas: ExcelFormula[];
  rules: Rule[];
  sheetAnalyses: SheetAnalysis[];
}

interface DeamFormulaMapping {
  ruleId: string;
  sourceCell: string;
  sourceFormula: string;
  expression: string;
}

interface DeamColumnRecommendation {
  sourceSheet: string;
  sourceColumn: string;
  sourceHeader: string;
  deamColumnName: string;
  originalDeamColumnName?: string;
  dataType: string;
  role: string;
  included: boolean;
  mandatory: boolean;
  formulaMapping?: DeamFormulaMapping | null;
  detectionReasons: string[];
}

interface StatusTransition {
  fromStatus: string;
  toStatuses: string[];
}

interface StatusLocation {
  sheet: string;
  column: string;
  header: string;
  deamColumnName: string;
}

interface StatusRulePlaceholder {
  ruleId?: string;
  event: string;
  fromStatus: string;
  toStatus: string;
  name: string;
  description: string;
  expression: string;
  enabled: boolean;
  source?: Record<string, unknown>;
  detectionReasons?: string[];
}

type RulePlaceholder = StatusRulePlaceholder;

interface DeamStatusFieldRecommendation {
  sourceSheet: string;
  sourceColumn: string;
  sourceHeader: string;
  fieldName: string;
  statusLocation: StatusLocation;
  included: boolean;
  confidence: number;
  detectionReasons: string[];
  allowedValues: string[];
  defaultValue?: string | null;
  transitions: StatusTransition[];
  rulePlaceholders?: StatusRulePlaceholder[];
}

interface DeamSheetSchemaRecommendation {
  sheetName: string;
  columns: DeamColumnRecommendation[];
  statusFields: DeamStatusFieldRecommendation[];
  businessRules: RulePlaceholder[];
}

interface DeamAnalysisResponse {
  fileName: string;
  selectedSheet: string;
  sheets: string[];
  columns: DeamColumnRecommendation[];
  statusFields: DeamStatusFieldRecommendation[];
  businessRules: RulePlaceholder[];
  sheetSchemas: DeamSheetSchemaRecommendation[];
}

interface SchemaValidationResult {
  errors: string[];
  warnings: string[];
}

interface FilePlan {
  fileName: string;
  mode: 'join' | 'individual';
  entityName: string;
  joinKey: string;
  sheetName: string;
  include: boolean;
}

type ColumnUpdateOperation =
  | { operation: 'RENAME_COLUMN'; columnName: string; newColumnName: string }
  | { operation: 'CHANGE_DATA_TYPE'; columnName: string; newDataType: string }
  | { operation: 'DROP_COLUMN'; columnName: string };

interface TranslationActionEvent {
  action: 'export' | 'save';
  columns: Array<{
    sourceColumn: string;
    sourceHeader: string;
    columnName: string;
    dataType: string;
  }>;
  changeCount: number;
}

interface FilePlanCheck {
  errors: string[];
  warnings: string[];
  joinedCount: number;
  individualCount: number;
  totalCount: number;
}

interface WorkflowDeamPreviewEntry {
  title: string;
  entityName: string;
  mode: string;
  primaryKey: string;
  fileNames: string[];
  sheetNames: string[];
  json: string;
}

interface WorkflowDeamPreview {
  mode?: string;
  entityName?: string;
  primaryKey?: string;
  fileNames?: string[];
  sheetNames?: string[];
  deamDefinitionJson?: Record<string, unknown> | Record<string, unknown>[];
}

interface WorkflowFilePreview {
  fileName?: string;
  sheetName?: string;
  sheets?: string[];
  mode?: string;
  extractionMode?: string;
  hasExtractionConfig?: boolean;
  joinKey?: string;
  entityName?: string;
  include?: boolean;
  resolvedPrimaryKey?: string;
  columnCount?: number;
  columns?: Array<{
    columnLetter?: string;
    header?: string;
    fieldName?: string;
    dataType?: string;
  }>;
}

interface WorkflowPreviewResponse {
  status?: string;
  workflowName?: string;
  entityName?: string;
  primaryKey?: string;
  workflowJson?: string;
  workflowConfig?: Record<string, unknown>;
  deamDefinitionJson?: Record<string, unknown>;
  deamDefinitions?: WorkflowDeamPreview[];
  validationMessages?: string[];
  files?: WorkflowFilePreview[];
  message?: string;
  filePlanDraft?: FilePlan[];
}

const AUDIT_ATTRIBUTES: Array<{ name: string; type: string; displayName: string }> = [
  { name: 'created_by', type: 'string', displayName: 'Created By' },
  { name: 'created_date', type: 'string', displayName: 'Created Date' },
  { name: 'updated_date', type: 'string', displayName: 'Updated Date' },
  { name: 'updated_by', type: 'string', displayName: 'Updated By' },
];

@Component({
  selector: 'app-rule-builder',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSnackBarModule, MatIconModule, MatTooltipModule],
  templateUrl: './rule-builder.component.html',
  styleUrl: './rule-builder.component.scss',
})
export class RuleBuilderComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly apiService = inject(SpricedLiteApiService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly configService = inject(SelfServiceConfigService);
  private readonly datasetService = inject(SelfServiceDatasetService);
  private readonly apiConfig = inject(SelfServiceApiConfigService);
  private readonly keycloak = inject(Keycloak, { optional: true });

  // ── JSON-driven layout ────────────────────────────────────────────────────
  static readonly STEP_FIELD_IDS: ReadonlySet<string> = new Set<string>([
    'intake',
    'workspace',
  ]);

  private static readonly DEFAULT_LAYOUT: BuilderLayoutConfig = {
    version: '1.0',
    builder: 'rule',
    steps: [
      { id: 'intake', label: 'Analyze workbook', sections: [{ id: 'intake', fields: [{ field: 'intake' }] }] },
      { id: 'workspace', label: 'Workspace', sections: [{ id: 'workspace', fields: [{ field: 'workspace' }] }] },
    ],
  };

  layout: BuilderLayoutConfig = RuleBuilderComponent.DEFAULT_LAYOUT;

  @Output() viewModeChange = new EventEmitter<'data' | 'charts' | 'dashboards' | 'rules'>();
  @Output() translationPreview = new EventEmitter<{
    columns: Array<{
      sourceColumn: string;
      sourceHeader: string;
      columnName: string;
      dataType: string;
    }>;
  }>();
  @Output() translationAction = new EventEmitter<TranslationActionEvent>();

  private viewInitialized = false;
  private layoutInitialized = false;
  private autoAnalyzeRequested = false;
  private autoAnalyzeStarted = false;
  private readonly initialDeamColumnKeysByFile = new Map<string, Set<string>>();

  @Input()
  set initialFiles(files: File[] | null | undefined) {
    this.initialDeamColumnKeysByFile.clear();
    this.workflowSourceDeamResponse.set(this.datasetService.getLatestWorkflowDeamResponse());
    this.configureSelectedFiles(files ? [...files] : []);
    this.tryAutoAnalyze();
  }

  @Input()
  set initialDeamResponse(response: unknown | null | undefined) {
    this.workflowSourceDeamResponse.set(response ?? null);
  }

  @Input()
  set autoAnalyze(value: boolean) {
    this.autoAnalyzeRequested = value;
    this.tryAutoAnalyze();
  }

  // ── File selection (multi-file) ───────────────────────────────────────────
  readonly selectedFiles = signal<File[]>([]);
  readonly selectedFile = computed(() => this.selectedFiles()[0] ?? null);

  // ── Basic analysis state ──────────────────────────────────────────────────
  readonly sheetName = signal('');
  readonly analysis = signal<ExcelAnalysisResponse | null>(null);
  readonly deamAnalysis = signal<DeamAnalysisResponse | null>(null);
  readonly activeSheetName = signal('');
  readonly selectedRule = signal<Rule | null>(null);
  readonly inputJson = signal('{\n  "quantity": 10,\n  "price": 100\n}');
  readonly outputJson = signal('');
  readonly runMode = signal<'selected' | 'all'>('selected');
  readonly globalRulePlaceholders = signal<Record<string, RulePlaceholder[]>>({});
  readonly showTestCalculation = signal(false);

  // ── Per-file analysis cache (enables workbook switching) ─────────────────
  readonly analysisCache = signal<Record<string, ExcelAnalysisResponse>>({});
  readonly deamAnalysisCache = signal<Record<string, DeamAnalysisResponse>>({});
  readonly ruleCache = signal<Record<string, Record<string, RulePlaceholder[]>>>({});
  readonly activeFileName = signal('');

  // ── Workflow preview (multi-file) ─────────────────────────────────────────
  readonly primaryKey = signal('');
  readonly entityName = signal('');
  readonly workflowName = signal('');
  readonly filePlans = signal<FilePlan[]>([]);
  readonly filePlanJsonDraft = signal('[]');
  readonly workflowPreview = signal<WorkflowPreviewResponse | null>(null);
  readonly workflowSourceDeamResponse = signal<unknown | null>(null);

  // ── Formula → rule builder ────────────────────────────────────────────────
  readonly formulaInput = signal('');
  readonly formulaOutputField = signal('result');
  readonly formulaCellMappingJson = signal('{}');
  readonly generatedRule = signal<Rule | null>(null);
  readonly translatingFormula = signal(false);

  // ── Loading / status ──────────────────────────────────────────────────────
  readonly loading = signal(false);
  readonly previewing = signal(false);
  readonly executing = signal(false);
  readonly persisting = signal(false);
  readonly error = signal('');
  readonly formulaError = signal('');
  readonly saveMessage = signal('');

  // ── Async ingestion progress ──────────────────────────────────────────────
  readonly ingestionJobId = signal<string | null>(null);
  readonly ingestionEstimate = signal('');
  readonly ingestionProgress = signal<Record<string, unknown> | null>(null);
  private pollSub: Subscription | null = null;

  ngOnInit(): void {
    this.configService
      .loadBuilderLayout('rule', RuleBuilderComponent.DEFAULT_LAYOUT)
      .subscribe((layout) => {
        const validation = this.configService.validateLayout(layout, RuleBuilderComponent.STEP_FIELD_IDS);
        if (validation.unknownFields.length) {
          console.warn('[rule-builder] config references unknown field ids; they will be skipped:', validation.unknownFields);
        }
        this.layout = layout;
        this.apiConfig.applyFromLayout(layout);
        this.layoutInitialized = true;
        this.tryAutoAnalyze();
      });
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.tryAutoAnalyze();
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
  }

  get acceptedFileTypes(): string {
    return (this.layout.options?.acceptedFileTypes as string | undefined) ?? '.xlsx,.xls,.xlsm';
  }

  private getUserId(): string {
    return (this.keycloak?.tokenParsed?.['preferred_username'] as string | undefined) ?? '';
  }

  private showSnack(message: string, type: 'success' | 'error'): void {
    this.snackBar.open(message, '✕', {
      duration: 4000,
      horizontalPosition: 'right',
      verticalPosition: 'bottom',
      panelClass: type === 'success' ? ['ss-snack-success'] : ['ss-snack-error'],
    });
  }

  private setSaveMessage(message: string, showSnackbar = true): void {
    this.saveMessage.set(message);
    if (message && showSnackbar) this.showSnack(message, 'success');
  }

  private setErrorMessage(message: string): void {
    this.error.set(message);
    if (message) this.showSnack(message, 'error');
  }

  // ── Computed signals ──────────────────────────────────────────────────────

  readonly activeSheet = computed(() => {
    const data = this.analysis();
    if (!data) return null;
    return data.sheetAnalyses.find((s) => s.sheetName === this.activeSheetName()) || data.sheetAnalyses[0] || null;
  });

  readonly selectedFormula = computed(() => {
    const rule = this.selectedRule();
    const sheet = this.activeSheet();
    if (!rule || !sheet) return null;
    return (
      sheet.formulas.find(
        (f) => f.ruleId === rule.ruleId && f.outputField === rule.outputField,
      ) || null
    );
  });

  readonly activeDeamSheet = computed(() => {
    const deam = this.deamAnalysis();
    if (!deam) return null;
    return deam.sheetSchemas.find((s) => s.sheetName === this.activeSheetName()) || deam.sheetSchemas[0] || null;
  });

  readonly finalDeamSchemaJson = computed(() => {
    const joinedPlans = this.joinedFilePlans();
    const cache = this.deamAnalysisCache();

    if (joinedPlans.length > 1) {
      // Prefer the backend-generated combined DEAM: it uses normalizeKey() for join-key matching
      // which is resilient to casing/spacing differences (e.g. "ProductID" vs "product_id").
      // buildMergedDeamEntity uses toSnakeCase() which can miss those matches.
      const backendDeam = this.workflowPreviewDeamJson();
      if (backendDeam) {
        return backendDeam;
      }
      // Fallback: build from cached per-file analyses when backend preview is unavailable
      const analyses = joinedPlans.map((p) => cache[p.fileName]).filter(Boolean) as DeamAnalysisResponse[];
      if (analyses.length === joinedPlans.length) {
        const merged = this.buildMergedDeamEntity(joinedPlans, analyses);
        const individualEntities = this.individualFilePlans()
          .map((p) => {
            const deam = cache[p.fileName];
            if (!deam) return null;
            const sheetName = p.sheetName.trim() || deam.selectedSheet;
            const sheet = deam.sheetSchemas.find((s) => s.sheetName === sheetName) || deam.sheetSchemas[0];
            return sheet ? this.buildDeamEntity(sheet, p.fileName) : null;
          })
          .filter(Boolean) as Record<string, unknown>[];
        return JSON.stringify([merged, ...individualEntities], null, 2);
      }
      return '';
    }

    const deam = this.deamAnalysis();
    if (deam) {
      return JSON.stringify(this.buildFinalDeamDefinitions(deam), null, 2);
    }
    return this.workflowPreviewDeamJson() || '';
  });

  readonly schemaValidation = computed<SchemaValidationResult>(() => {
    const sheet = this.activeDeamSheet();
    if (!sheet) return { errors: [], warnings: [] };

    const errors: string[] = [];
    const warnings: string[] = [];
    const includedColumns = sheet.columns.filter((c) => c.included);
    const names = new Map<string, number>();

    for (const col of includedColumns) {
      const normalizedName = col.deamColumnName.trim().toLowerCase();
      if (!normalizedName) {
        errors.push(`Column from ${col.sourceColumn} has an empty DEAM column name.`);
        continue;
      }
      names.set(normalizedName, (names.get(normalizedName) || 0) + 1);
      if (col.mandatory && col.role === 'formula') {
        warnings.push(`${col.deamColumnName} is mandatory but calculated by formula.`);
      }
      if (col.mandatory && col.role === 'derived') {
        warnings.push(`${col.deamColumnName} is mandatory but marked as derived.`);
      }
    }

    for (const [name, count] of names.entries()) {
      if (count > 1) errors.push(`Duplicate DEAM column name: ${name}.`);
    }

    for (const status of sheet.statusFields.filter((f) => f.included)) {
      const statusColumn = this.findStatusColumn(sheet, status);
      if (!statusColumn) {
        errors.push(
          `Status field ${status.fieldName || status.sourceHeader} is included, but its DEAM status column is not included.`,
        );
      }
      if (!status.fieldName.trim()) {
        errors.push(`Status field ${status.sourceHeader} has an empty field name.`);
      }
      if (!status.allowedValues.length) {
        errors.push(`Status field ${status.fieldName || status.sourceHeader} must have at least one allowed value.`);
      }
      if (status.defaultValue && !status.allowedValues.includes(status.defaultValue)) {
        errors.push(`Default status "${status.defaultValue}" is not allowed for ${status.fieldName}.`);
      }
      for (const transition of status.transitions) {
        if (!status.allowedValues.includes(transition.fromStatus)) {
          errors.push(`Transition source "${transition.fromStatus}" is not allowed for ${status.fieldName}.`);
        }
        for (const target of transition.toStatuses) {
          if (!status.allowedValues.includes(target)) {
            errors.push(`Transition target "${target}" is not allowed for ${status.fieldName}.`);
          }
        }
      }
      for (const placeholder of status.rulePlaceholders || []) {
        if (!placeholder.name.trim()) {
          errors.push(`Rule on ${status.fieldName} needs a name.`);
        }
        if (placeholder.fromStatus && !status.allowedValues.includes(placeholder.fromStatus)) {
          errors.push(`Rule "${placeholder.name}" has invalid from status "${placeholder.fromStatus}".`);
        }
        if (placeholder.toStatus && !status.allowedValues.includes(placeholder.toStatus)) {
          errors.push(`Rule "${placeholder.name}" has invalid to status "${placeholder.toStatus}".`);
        }
      }
    }

    for (const placeholder of this.activeGlobalRulePlaceholders()) {
      if (!placeholder.name.trim()) errors.push('Sheet-level rule needs a name.');
    }

    return { errors, warnings };
  });

  // ── Workflow preview computed ─────────────────────────────────────────────

  readonly workflowPreviewJson = computed(() => {
    const preview = this.workflowPreview();
    if (!preview) return '';
    if (preview.workflowJson?.trim()) return preview.workflowJson;
    if (preview.workflowConfig) return JSON.stringify(preview.workflowConfig, null, 2);
    return '';
  });

  readonly workflowPreviewDeamJson = computed(() => {
    const preview = this.workflowPreview();
    if (!preview?.deamDefinitionJson) return '';
    return JSON.stringify(preview.deamDefinitionJson, null, 2);
  });

  readonly workflowPreviewDeamEntries = computed<WorkflowDeamPreviewEntry[]>(() => {
    const preview = this.workflowPreview();
    const deamDefinitions = preview?.deamDefinitions || [];
    if (deamDefinitions.length) {
      return deamDefinitions.map((entry, index) => ({
        title: this.deamPreviewEntryTitle(entry, index),
        entityName: entry.entityName || '',
        mode: entry.mode || '',
        primaryKey: entry.primaryKey || '',
        fileNames: entry.fileNames || [],
        sheetNames: entry.sheetNames || [],
        json: JSON.stringify(entry.deamDefinitionJson || {}, null, 2),
      }));
    }
    if (!preview?.deamDefinitionJson) return [];
    return [{
      title: 'DEAM Definition JSON',
      entityName: preview.entityName || '',
      mode: 'primary',
      primaryKey: preview.primaryKey || '',
      fileNames: [],
      sheetNames: [],
      json: JSON.stringify(preview.deamDefinitionJson, null, 2),
    }];
  });

  readonly deamPreviewTitle = computed(() => {
    const count = this.workflowPreviewDeamEntries().length;
    return count > 1 ? `DEAM Definition JSONs (${count})` : 'DEAM Definition JSON';
  });

  readonly joinedFilePlans = computed(() => this.filePlans().filter((p) => p.mode === 'join' && p.include !== false));
  readonly individualFilePlans = computed(() => this.filePlans().filter((p) => p.mode === 'individual'));

  readonly workflowPreviewSummary = computed(() => {
    const preview = this.workflowPreview();
    const fileCount = preview?.files?.length || 0;
    return fileCount > 1
      ? `Combined workflow generated from ${fileCount} uploaded files.`
      : 'Workflow generated from the uploaded workbook.';
  });

  readonly joinedExportSummary = computed(() => {
    const joinedPlans = this.joinedFilePlans();
    if (joinedPlans.length < 2) return 'Mark two or more files as Join to enable this export.';
    const joinKey = joinedPlans.map((p) => p.joinKey.trim()).find(Boolean) || this.primaryKey().trim() || 'uuid';
    return `Combined entity using ${joinKey} from ${joinedPlans.map((p) => p.fileName).join(', ')}.`;
  });

  readonly preparedExportSummary = computed(() => {
    const sheet = this.activeDeamSheet();
    const includedColumns = sheet?.columns.filter((c) => c.included).length || 0;
    const statusFields = sheet?.statusFields.filter((s) => s.included).length || 0;
    return `${includedColumns} included columns, ${statusFields} status fields, ${this.schemaValidation().errors.length} validation errors.`;
  });

  readonly finalDataExportSummary = computed(() => {
    const joinedPlans = this.joinedFilePlans();
    const sheet = this.activeDeamSheet();
    const includedColumns = sheet?.columns.filter((c) => c.included).length || 0;
    const joinLabel = joinedPlans.length > 1 ? `${joinedPlans.length} joined files` : 'prepared data';
    return `${joinLabel}, ${includedColumns} included columns, ${this.schemaValidation().errors.length} validation errors.`;
  });
  readonly filePlanCheck = computed<FilePlanCheck>(() => {
    const plans = this.filePlans();
    const draft = this.filePlanJsonDraft();
    const previews = this.workflowPreview()?.files || [];
    const errors: string[] = [];
    const warnings: string[] = [];
    const joinedCount = plans.filter((p) => p.mode === 'join' && p.include).length;
    const individualCount = plans.filter((p) => p.mode === 'individual' && p.include).length;
    const totalCount = plans.length;

    if (!plans.length) {
      return { errors, warnings, joinedCount, individualCount, totalCount };
    }

    try {
      const parsed = JSON.parse(draft) as unknown;
      if (!Array.isArray(parsed)) {
        errors.push('File plan JSON must be an array.');
      } else {
        const normalizedDraft = parsed.map((e) => this.normalizeFilePlanEntry(e));
        const normalizedCards = plans.map((e) => this.normalizeFilePlanEntry(e));
        if (JSON.stringify(normalizedDraft) !== JSON.stringify(normalizedCards)) {
          errors.push('File plan JSON does not match the configured file cards.');
        }
      }
    } catch {
      errors.push('File plan JSON is invalid.');
    }

    if (joinedCount === 0 && individualCount > 1) {
      warnings.push('No files are marked for join, so the workflow will generate separate reference entities.');
    }

    for (const plan of plans) {
      const filePreview = previews.find(
        (p) => String(p.fileName || '').trim().toLowerCase() === plan.fileName.trim().toLowerCase(),
      );
      const requestedSheet = plan.sheetName.trim();
      if (!requestedSheet || !filePreview?.sheets?.length) continue;
      const normalizedSheets = filePreview.sheets.map((s) => s.trim().toLowerCase());
      if (!normalizedSheets.includes(requestedSheet.toLowerCase())) {
        errors.push(
          `Sheet "${plan.sheetName}" was not found in ${plan.fileName}. Available: ${filePreview.sheets.join(', ')}.`,
        );
      }
    }

    return { errors, warnings, joinedCount, individualCount, totalCount };
  });

  // ── File selection ────────────────────────────────────────────────────────

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const newFiles = Array.from(input.files || []);
    input.value = '';
    if (!newFiles.length) return;

    // Append to existing files so "+" button adds rather than replaces
    const files = [...this.selectedFiles(), ...newFiles];
    this.selectedFiles.set(files);
    this.error.set('');
    const pk = this.primaryKey();
    const plans: FilePlan[] = files.map((f) => ({
      fileName: f.name,
      // Single file → auto-select "individual" (each sheet as separate entity)
      mode: files.length === 1 ? 'individual' : 'join',
      entityName: this.toSnakeCase(f.name.replace(/\.[^.]+$/, '')),
      joinKey: pk,
      sheetName: this.sheetName() || '',
      include: true,
    }));
    this.filePlans.set(plans);
    this.filePlanJsonDraft.set(JSON.stringify(plans, null, 2));
    if (files.length === 1) {
      const name = files[0].name.replace(/\.[^.]+$/, '');
      if (!this.entityName()) this.entityName.set(this.toSnakeCase(name));
      if (!this.workflowName()) this.workflowName.set(this.toSnakeCase(name));
    }
  }

  private configureSelectedFiles(files: File[]): void {
    this.selectedFiles.set(files);
    this.error.set('');
    const pk = this.primaryKey();
    const plans: FilePlan[] = files.map((file) => ({
      fileName: file.name,
      mode: files.length === 1 ? 'individual' : 'join',
      entityName: this.toSnakeCase(file.name.replace(/\.[^.]+$/, '')),
      joinKey: pk,
      sheetName: this.sheetName() || '',
      include: true,
    }));
    this.filePlans.set(plans);
    this.filePlanJsonDraft.set(JSON.stringify(plans, null, 2));

    if (files.length === 1) {
      const name = files[0].name.replace(/\.[^.]+$/, '');
      if (!this.entityName()) this.entityName.set(this.toSnakeCase(name));
      if (!this.workflowName()) this.workflowName.set(this.toSnakeCase(name));
    }
  }

  private tryAutoAnalyze(): void {
    if (
      !this.autoAnalyzeRequested
      || this.autoAnalyzeStarted
      || !this.viewInitialized
      || !this.layoutInitialized
      || !this.selectedFiles().length
    ) {
      return;
    }

    this.autoAnalyzeStarted = true;
    if (this.hydrateTranslationFromWorkflowDeamResponse()) {
      return;
    }
    queueMicrotask(() => this.analyzeWorkbook());
  }

  private hydrateTranslationFromWorkflowDeamResponse(): boolean {
    const payload = this.resolveValidateAndSavePayload(this.workflowSourceDeamResponse());
    if (!payload) return false;

    const deam = payload['deam'];
    if (!deam || typeof deam !== 'object') return false;

    const deamRecord = deam as Record<string, unknown>;
    const attributes = deamRecord['attributesList'];
    if (!Array.isArray(attributes)) return false;

    const files = this.selectedFiles();
    const fileName = files[0]?.name || String(payload['entityName'] || deamRecord['entity'] || 'Uploaded source');
    const entity = String(payload['entityName'] || deamRecord['entity'] || this.toSnakeCase(fileName.replace(/\.[^.]+$/, '')));
    const sheetName = String(deamRecord['displayName'] || entity || 'DEAM');

    const editableAttributes = attributes
      .filter((attribute): attribute is Record<string, unknown> => (
        !!attribute
        && typeof attribute === 'object'
        && !Array.isArray(attribute)
        && this.isWorkflowSourceEditableAttribute(attribute as Record<string, unknown>)
      ));

    const columns: DeamColumnRecommendation[] = editableAttributes.map((attribute, index) => {
      const name = String(attribute['name'] || `column_${index + 1}`);
      const displayName = String(attribute['displayName'] || name);
      return {
        sourceSheet: sheetName,
        sourceColumn: this.excelColumnLabel(index),
        sourceHeader: displayName,
        deamColumnName: name,
        originalDeamColumnName: name,
        dataType: this.toUiDataType(String(attribute['type'] || 'string')),
        role: 'input',
        included: true,
        mandatory: Boolean(attribute['mandatory']),
        formulaMapping: null,
        detectionReasons: ['Loaded from getDEAM response'],
      };
    });

    const sheet: DeamSheetSchemaRecommendation = {
      sheetName,
      columns,
      statusFields: [],
      businessRules: [],
    };
    const deamAnalysis: DeamAnalysisResponse = {
      fileName,
      selectedSheet: sheetName,
      sheets: [sheetName],
      columns,
      statusFields: [],
      businessRules: [],
      sheetSchemas: [sheet],
    };
    const excelAnalysis: ExcelAnalysisResponse = {
      fileName,
      selectedSheet: sheetName,
      sheets: [sheetName],
      columns: columns.map((column) => ({
        columnLetter: column.sourceColumn,
        header: column.sourceHeader,
        fieldName: column.deamColumnName,
        dataType: column.dataType,
        sampleValue: null,
      })),
      formulas: [],
      rules: [],
      sheetAnalyses: [{
        sheetName,
        columns: columns.map((column) => ({
          columnLetter: column.sourceColumn,
          header: column.sourceHeader,
          fieldName: column.deamColumnName,
          dataType: column.dataType,
          sampleValue: null,
        })),
        formulas: [],
        rules: [],
      }],
    };

    this.activeFileName.set(fileName);
    this.analysis.set(excelAnalysis);
    this.deamAnalysis.set(deamAnalysis);
    this.analysisCache.set({ [fileName]: excelAnalysis });
    this.deamAnalysisCache.set({ [fileName]: deamAnalysis });
    this.activeSheetName.set(sheetName);
    this.entityName.set(entity);
    this.workflowName.set(entity);
    this.workflowPreview.set(null);
    this.globalRulePlaceholders.set({});
    this.ruleCache.set({ [fileName]: {} });
    this.selectedRule.set(null);
    this.inputJson.set(this.buildSampleInput(excelAnalysis.columns));
    this.outputJson.set('');
    this.loading.set(false);
    this.error.set('');
    this.captureInitialDeamColumnKeys(fileName, deamAnalysis);
    return true;
  }

  private isWorkflowSourceEditableAttribute(attribute: Record<string, unknown>): boolean {
    const name = String(attribute['name'] || '').trim().toLowerCase();
    if (!name) return false;
    if (name === 'id' || name === 'uuid') return false;
    if (AUDIT_ATTRIBUTES.some((audit) => audit.name === name)) return false;
    return true;
  }

  private toUiDataType(deamType: string): string {
    const normalizedType = deamType.trim().toLowerCase();
    if (normalizedType === 'integer' || normalizedType === 'long' || normalizedType === 'bigint') return 'INTEGER';
    if (normalizedType === 'numeric' || normalizedType === 'number' || normalizedType === 'decimal' || normalizedType === 'double') return 'NUMERIC';
    if (normalizedType === 'boolean') return 'BOOLEAN';
    if (normalizedType === 'date') return 'DATE';
    if (normalizedType === 'datetime' || normalizedType === 'timestamp') return 'TIMESTAMP';
    return 'TEXT';
  }

  private excelColumnLabel(index: number): string {
    let value = index + 1;
    let label = '';
    while (value > 0) {
      const remainder = (value - 1) % 26;
      label = String.fromCharCode(65 + remainder) + label;
      value = Math.floor((value - 1) / 26);
    }
    return label;
  }

  removeFile(index: number): void {
    const files = [...this.selectedFiles()];
    const removedFileName = files[index]?.name || '';
    files.splice(index, 1);
    if (removedFileName) this.initialDeamColumnKeysByFile.delete(removedFileName);
    this.selectedFiles.set(files);
    if (files.length === 0) {
      this.filePlans.set([]);
      this.filePlanJsonDraft.set('[]');
      this.error.set('');
      this.analysis.set(null);
      this.deamAnalysis.set(null);
      this.entityName.set('');
      this.workflowName.set('');
    } else {
      const pk = this.primaryKey();
      const plans: FilePlan[] = files.map((f) => ({
        fileName: f.name,
        mode: files.length === 1 ? 'individual' : 'join',
        entityName: this.toSnakeCase(f.name.replace(/\.[^.]+$/, '')),
        joinKey: pk,
        sheetName: this.sheetName() || '',
        include: true,
      }));
      this.filePlans.set(plans);
      this.filePlanJsonDraft.set(JSON.stringify(plans, null, 2));
    }
  }

  updatePrimaryKey(value: string): void {
    this.primaryKey.set(value);
    const plans = this.filePlans().map((p) => ({ ...p, joinKey: value }));
    this.filePlans.set(plans);
    this.filePlanJsonDraft.set(JSON.stringify(plans, null, 2));
  }

  // ── Workbook switching ────────────────────────────────────────────────────

  selectWorkbookFile(fileName: string): void {
    if (fileName === this.activeFileName()) return;
    // Save current deam edits + rules back to cache before switching
    const currentDeam = this.deamAnalysis();
    if (currentDeam) {
      this.deamAnalysisCache.update((c) => ({ ...c, [this.activeFileName()]: currentDeam }));
    }
    this.ruleCache.update((r) => ({ ...r, [this.activeFileName()]: this.globalRulePlaceholders() }));

    const excel = this.analysisCache()[fileName];
    const deam = this.deamAnalysisCache()[fileName];
    if (!excel || !deam) return;

    this.activeFileName.set(fileName);
    this.analysis.set(excel);
    this.deamAnalysis.set(deam);
    this.activeSheetName.set(excel.selectedSheet);
    this.globalRulePlaceholders.set(
      this.ruleCache()[fileName] || this.initializeGlobalRulePlaceholders(deam),
    );
    this.selectedRule.set(excel.rules[0] || null);
    this.inputJson.set(this.buildSampleInput(excel.columns));
    this.outputJson.set('');
  }

  // ── Combined analyze + preview ────────────────────────────────────────────

  analyzeWorkbook(): void {
    const files = this.selectedFiles();
    if (!files.length) return;

    const requestedSheet = this.sheetName().trim();
    const entity = this.entityName().trim() || this.toSnakeCase(files[0].name.replace(/\.[^.]+$/, ''));
    const workflow = this.workflowName().trim() || entity;
    const pk = this.primaryKey().trim() || 'uuid';
    const filePlanJson = this.filePlans().length ? JSON.stringify(this.filePlans()) : null;

    this.loading.set(true);
    this.error.set('');

    const localAnalyses = files.map((file) => this.analyzeWorkbookLocally(file, requestedSheet));
    void localAnalyses[0]
      .then(({ excel, deam }) => {
        if (!this.loading() || this.deamAnalysis()) return;
        this.activeFileName.set(files[0].name);
        this.analysis.set(excel);
        this.deamAnalysis.set(deam);
        this.activeSheetName.set(excel.selectedSheet);
        this.captureInitialDeamColumnKeys(files[0].name, deam);
        this.globalRulePlaceholders.set(this.initializeGlobalRulePlaceholders(deam));
        this.selectedRule.set(excel.rules[0] || null);
        this.inputJson.set(this.buildSampleInput(excel.columns));
      })
      .catch(() => undefined);

    // Analyze all uploaded files in parallel + workflow preview
    forkJoin({
      analyses: forkJoin(
        files.map((f, index) =>
          forkJoin({
            excel: this.apiService.analyzeWorkbook(f, requestedSheet || null).pipe(catchError(() => of(null))),
            deam: this.apiService.analyzeDeamWorkbook(f, requestedSheet || null).pipe(catchError(() => of(null))),
            local: from(localAnalyses[index]).pipe(catchError(() => of(null))),
          }),
        ),
      ),
      preview: this.apiService.previewWorkflow({
        files,
        workflowName: workflow,
        entityName: entity,
        primaryKey: pk,
        userId: this.getUserId(),
        sheetName: requestedSheet || null,
        filePlanJson,
      }).pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ analyses, preview }) => {
        const excelCache: Record<string, ExcelAnalysisResponse> = {};
        const deamCache: Record<string, DeamAnalysisResponse> = {};
        const rCache: Record<string, Record<string, RulePlaceholder[]>> = {};

        analyses.forEach(({ excel, deam, local }, idx) => {
          const localAnalysis = local as { excel: ExcelAnalysisResponse; deam: DeamAnalysisResponse } | null;
          const er = (excel as unknown as ExcelAnalysisResponse | null)
            ?? localAnalysis?.excel
            ?? (deam ? this.buildExcelAnalysisFromDeam(deam as unknown as DeamAnalysisResponse) : null);
          const dr = (deam as unknown as DeamAnalysisResponse | null)
            ?? localAnalysis?.deam
            ?? (er ? this.buildDeamAnalysisFromExcel(er) : null);

          if (er && dr) {
            excelCache[files[idx].name] = er;
            deamCache[files[idx].name] = dr;
            this.captureInitialDeamColumnKeys(files[idx].name, dr);
            rCache[files[idx].name] = this.initializeGlobalRulePlaceholders(dr);
          }
        });

        this.analysisCache.set(excelCache);
        this.deamAnalysisCache.set(deamCache);
        this.ruleCache.set(rCache);

        // Activate first successfully-analyzed file
        const firstName = files.find((f) => excelCache[f.name])?.name ?? '';
        if (firstName) {
          this.activeFileName.set(firstName);
          this.analysis.set(excelCache[firstName]);
          this.deamAnalysis.set(deamCache[firstName]);
          this.activeSheetName.set(excelCache[firstName].selectedSheet);
          this.globalRulePlaceholders.set(rCache[firstName] || {});
          this.selectedRule.set(excelCache[firstName].rules[0] || null);
          this.inputJson.set(this.buildSampleInput(excelCache[firstName].columns));
          this.outputJson.set('');
        } else {
          this.setErrorMessage('Workbook analysis failed. Check that the backend is reachable.');
        }

        if (preview) {
          const previewResult = preview as unknown as WorkflowPreviewResponse;
          this.workflowPreview.set(previewResult);
          if (previewResult.filePlanDraft?.length) {
            this.filePlans.set(previewResult.filePlanDraft);
            this.filePlanJsonDraft.set(JSON.stringify(previewResult.filePlanDraft, null, 2));
          }
        }

        if (firstName) {
          this.setSaveMessage(
            preview
              ? `Analyzed ${files.length} file(s) and generated workflow preview.`
              : `Analyzed ${files.length} file(s).`,
          );
        }
        this.loading.set(false);
      },
      error: (err) => {
        this.setErrorMessage(this.describeHttpError(err, 'Unable to analyze workbook.'));
        this.loading.set(false);
      },
    });
  }

  private async analyzeWorkbookLocally(
    file: File,
    requestedSheet: string,
  ): Promise<{ excel: ExcelAnalysisResponse; deam: DeamAnalysisResponse }> {
    const XLSX = await import('xlsx');
    const workbook = XLSX.read(await file.arrayBuffer(), {
      type: 'array',
      cellFormula: true,
      cellDates: true,
    });
    const sheetName = requestedSheet && workbook.SheetNames.includes(requestedSheet)
      ? requestedSheet
      : workbook.SheetNames[0];
    if (!sheetName) throw new Error('The workbook does not contain a sheet.');

    const rows = XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[sheetName], {
      header: 1,
      defval: null,
      raw: true,
    });
    const rawHeaders = rows[0] ?? [];
    let lastPopulatedColumn = rawHeaders.length - 1;
    while (
      lastPopulatedColumn >= 0
      && !rows.some((row) => {
        const value = row[lastPopulatedColumn];
        return value !== null && value !== undefined && String(value).trim() !== '';
      })
    ) {
      lastPopulatedColumn -= 1;
    }
    const headers = rawHeaders.slice(0, lastPopulatedColumn + 1).map((value, index) => {
      const header = String(value ?? '').trim();
      return header || `Column ${XLSX.utils.encode_col(index)}`;
    });
    if (!headers.length) throw new Error('The selected sheet does not contain columns.');

    const columns: ExcelColumn[] = headers.map((header, index) => {
      const sampleValue = rows
        .slice(1)
        .map((row) => row[index])
        .find((value) => value !== null && value !== undefined && value !== '');
      return {
        columnLetter: XLSX.utils.encode_col(index),
        header,
        fieldName: this.toSnakeCase(header),
        dataType: this.inferLocalColumnType(sampleValue),
        sampleValue: sampleValue ?? null,
      };
    });
    const sheetAnalysis: SheetAnalysis = {
      sheetName,
      columns,
      formulas: [],
      rules: [],
    };
    const excel: ExcelAnalysisResponse = {
      fileName: file.name,
      selectedSheet: sheetName,
      sheets: [...workbook.SheetNames],
      columns,
      formulas: [],
      rules: [],
      sheetAnalyses: [sheetAnalysis],
    };
    return { excel, deam: this.buildDeamAnalysisFromExcel(excel) };
  }

  private buildDeamAnalysisFromExcel(excel: ExcelAnalysisResponse): DeamAnalysisResponse {
    const sheetSchemas = excel.sheetAnalyses.map((sheet) => {
      const columns = sheet.columns.map((column) => ({
        sourceSheet: sheet.sheetName,
        sourceColumn: column.columnLetter,
        sourceHeader: column.header,
        deamColumnName: column.fieldName || this.toSnakeCase(column.header),
        originalDeamColumnName: column.fieldName || this.toSnakeCase(column.header),
        dataType: column.dataType,
        role: 'input',
        included: true,
        mandatory: false,
        formulaMapping: null,
        detectionReasons: ['Detected from the uploaded workbook'],
      }));
      return {
        sheetName: sheet.sheetName,
        columns,
        statusFields: [],
        businessRules: [],
      };
    });
    const selected = sheetSchemas.find((sheet) => sheet.sheetName === excel.selectedSheet)
      ?? sheetSchemas[0];
    return {
      fileName: excel.fileName,
      selectedSheet: selected?.sheetName ?? excel.selectedSheet,
      sheets: [...excel.sheets],
      columns: selected?.columns ?? [],
      statusFields: [],
      businessRules: [],
      sheetSchemas,
    };
  }

  private buildExcelAnalysisFromDeam(deam: DeamAnalysisResponse): ExcelAnalysisResponse {
    const sheetAnalyses = deam.sheetSchemas.map((sheet) => ({
      sheetName: sheet.sheetName,
      columns: sheet.columns.map((column) => ({
        columnLetter: column.sourceColumn,
        header: column.sourceHeader,
        fieldName: column.deamColumnName,
        dataType: column.dataType,
        sampleValue: null,
      })),
      formulas: [],
      rules: [],
    }));
    const selected = sheetAnalyses.find((sheet) => sheet.sheetName === deam.selectedSheet)
      ?? sheetAnalyses[0];
    return {
      fileName: deam.fileName,
      selectedSheet: selected?.sheetName ?? deam.selectedSheet,
      sheets: [...deam.sheets],
      columns: selected?.columns ?? [],
      formulas: [],
      rules: [],
      sheetAnalyses,
    };
  }

  private inferLocalColumnType(value: unknown): string {
    if (value instanceof Date) return 'DATE';
    if (typeof value === 'boolean') return 'BOOLEAN';
    if (typeof value === 'number') return Number.isInteger(value) ? 'INTEGER' : 'NUMERIC';
    return 'TEXT';
  }

  /** Set from the click until the host takes over — emitting is instant, but the
   *  host then blocks mapping rows, so the button must not look idle. */
  readonly applyingPreview = signal(false);

  previewTranslationConfiguration(): void {
    this.applyingPreview.set(true);
    const columns = (this.activeDeamSheet()?.columns ?? [])
      .filter((column) => column.included !== false)
      .map((column) => ({
        sourceColumn: column.sourceColumn,
        sourceHeader: column.sourceHeader,
        columnName: column.deamColumnName,
        dataType: column.dataType,
      }));
    this.translationPreview.emit({ columns });
  }

  // ── Multi-file workflow re-preview (after file plan changes) ──────────────

  previewWorkflowConfig(): void {
    const files = this.selectedFiles();
    if (!files.length) return;

    const entity = this.entityName().trim() || this.toSnakeCase(files[0].name.replace(/\.[^.]+$/, ''));
    const workflow = this.workflowName().trim() || entity;
    const pk = this.primaryKey().trim() || 'uuid';
    const filePlanJson = this.filePlans().length ? JSON.stringify(this.filePlans()) : null;

    this.previewing.set(true);
    this.error.set('');

    this.apiService.previewWorkflow({
      files,
      workflowName: workflow,
      entityName: entity,
      primaryKey: pk,
      userId: this.getUserId(),
      sheetName: this.sheetName().trim() || null,
      filePlanJson,
    }).subscribe({
      next: (response) => {
        const previewResult = response as unknown as WorkflowPreviewResponse;
        this.workflowPreview.set(previewResult);
        if (previewResult.filePlanDraft?.length) {
          this.filePlans.set(previewResult.filePlanDraft);
          this.filePlanJsonDraft.set(JSON.stringify(previewResult.filePlanDraft, null, 2));
        }
        this.previewing.set(false);
        this.setSaveMessage('Workflow preview ready.');
      },
      error: (err) => {
        this.setErrorMessage(this.describeHttpError(err, 'Workflow preview failed.'));
        this.previewing.set(false);
      },
    });
  }

  // ── File plan ─────────────────────────────────────────────────────────────

  updateFilePlan<K extends keyof FilePlan>(index: number, key: K, value: FilePlan[K]): void {
    const plans = [...this.filePlans()];
    if (!plans[index]) return;
    plans[index] = { ...plans[index], [key]: value };
    this.filePlans.set(plans);
    this.filePlanJsonDraft.set(JSON.stringify(plans, null, 2));
  }

  updateFilePlanJsonDraft(value: string): void {
    this.filePlanJsonDraft.set(value);
  }

  applyFilePlanJsonDraft(): void {
    try {
      const plans = JSON.parse(this.filePlanJsonDraft()) as FilePlan[];
      this.filePlans.set(plans);
      this.setSaveMessage('File plan applied from JSON.');
    } catch {
      this.setErrorMessage('File plan JSON is invalid.');
    }
  }

  previewForFile(fileName: string): WorkflowFilePreview | null {
    return (
      this.workflowPreview()?.files?.find(
        (f) => String(f.fileName || '').trim().toLowerCase() === fileName.trim().toLowerCase(),
      ) || null
    );
  }

  private deamPreviewEntryTitle(entry: WorkflowDeamPreview, index: number): string {
    if (entry.entityName) return entry.entityName;
    if (entry.fileNames?.length) return entry.fileNames[0];
    return `Entity ${index + 1}`;
  }

  private normalizeFilePlanEntry(entry: unknown): unknown {
    if (!entry || typeof entry !== 'object') return entry;
    const e = entry as Record<string, unknown>;
    return {
      fileName: String(e['fileName'] || '').trim(),
      mode: String(e['mode'] || 'join').trim(),
      entityName: String(e['entityName'] || '').trim(),
      joinKey: String(e['joinKey'] || '').trim(),
      sheetName: String(e['sheetName'] || '').trim(),
      include: e['include'] !== false,
    };
  }

  // ── Sheet / rule navigation ───────────────────────────────────────────────

  selectRule(rule: Rule): void {
    this.selectedRule.set(rule);
    const sheet = this.activeSheet();
    if (sheet) this.inputJson.set(this.buildSampleInput(sheet.columns));
    this.outputJson.set('');
    setTimeout(() => {
      document.getElementById('formulaMappingPanel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  selectSheet(sheet: SheetAnalysis): void {
    this.activeSheetName.set(sheet.sheetName);
    this.selectedRule.set(sheet.rules[0] || null);
    this.inputJson.set(this.buildSampleInput(sheet.columns));
    this.outputJson.set('');
    this.error.set('');
  }

  // ── Formula → rule builder ────────────────────────────────────────────────

  useSelectedFormulaInBuilder(): void {
    const formula = this.selectedFormula();
    if (!formula) return;
    this.formulaInput.set(formula.formula);
    this.formulaOutputField.set(formula.outputField);
    const sheet = this.activeSheet();
    if (sheet) {
      this.formulaCellMappingJson.set(this.apiService.buildSheetCellMapping(sheet.columns));
    }
  }

  generateRuleFromFormula(): void {
    const formula = this.formulaInput();
    const outputField = this.formulaOutputField();
    if (!formula || !outputField) {
      this.formulaError.set('Formula and output field are required.');
      return;
    }
    let cellMapping: Record<string, string> = {};
    const mappingJson = this.formulaCellMappingJson();
    if (mappingJson.trim()) {
      try {
        cellMapping = this.apiService.parseCellMapping(mappingJson);
      } catch {
        this.formulaError.set('Cell mapping JSON is invalid.');
        return;
      }
    }
    this.translatingFormula.set(true);
    this.formulaError.set('');
    this.apiService.createRuleFromFormula(formula, outputField, cellMapping).subscribe({
      next: (response) => {
        this.generatedRule.set(response as unknown as Rule);
        this.translatingFormula.set(false);
        this.setSaveMessage('Rule generated from formula.');
      },
      error: (err) => {
        this.formulaError.set(this.describeHttpError(err, 'Formula translation failed.'));
        this.translatingFormula.set(false);
      },
    });
  }

  // ── DEAM schema editing ───────────────────────────────────────────────────

  updateDeamColumn<K extends keyof DeamColumnRecommendation>(
    index: number,
    key: K,
    value: DeamColumnRecommendation[K],
  ): void {
    const deam = this.cloneDeamAnalysis();
    const sheet = this.findMutableDeamSheet(deam);
    if (!sheet?.columns[index]) return;
    if (!sheet.columns[index].originalDeamColumnName) {
      sheet.columns[index].originalDeamColumnName = sheet.columns[index].deamColumnName || this.toSnakeCase(sheet.columns[index].sourceHeader);
    }
    sheet.columns[index][key] = value;
    this.deamAnalysis.set(deam);
  }

  removeDeamColumn(index: number): void {
    const deam = this.cloneDeamAnalysis();
    const sheet = this.findMutableDeamSheet(deam);
    if (!sheet?.columns[index]) return;
    sheet.columns.splice(index, 1);
    this.deamAnalysis.set(deam);
    this.setSaveMessage('Column removed.');
  }

  trackByDeamColumn(_index: number, column: DeamColumnRecommendation): string {
    return `${column.sourceSheet}:${column.sourceColumn}`;
  }

  updateStatusField<K extends keyof DeamStatusFieldRecommendation>(
    index: number,
    key: K,
    value: DeamStatusFieldRecommendation[K],
  ): void {
    const deam = this.cloneDeamAnalysis();
    const sheet = this.findMutableDeamSheet(deam);
    if (!sheet?.statusFields[index]) return;
    sheet.statusFields[index][key] = value;
    this.deamAnalysis.set(deam);
  }

  updateStatusAllowedValues(index: number, rawValue: string): void {
    const allowedValues = this.parseCsvValues(rawValue);
    const deam = this.cloneDeamAnalysis();
    const sheet = this.findMutableDeamSheet(deam);
    if (!sheet?.statusFields[index]) return;
    sheet.statusFields[index].allowedValues = allowedValues;
    sheet.statusFields[index].transitions = this.buildTransitions(allowedValues);
    if (!allowedValues.includes(sheet.statusFields[index].defaultValue || '')) {
      sheet.statusFields[index].defaultValue = allowedValues[0] || null;
    }
    this.deamAnalysis.set(deam);
  }

  activeGlobalRulePlaceholders(): RulePlaceholder[] {
    return this.globalRulePlaceholders()[this.activeSheetName()] || [];
  }

  addGlobalRulePlaceholder(): void {
    const sheetName = this.activeSheetName();
    if (!sheetName) return;
    const current = this.globalRulePlaceholders();
    const rules = current[sheetName] || [];
    this.globalRulePlaceholders.set({
      ...current,
      [sheetName]: [
        ...rules,
        {
          event: 'on_calculation',
          fromStatus: '',
          toStatus: '',
          name: `Rule for ${this.toSnakeCase(sheetName)}`,
          description: '',
          expression: '',
          enabled: false,
        },
      ],
    });
    this.setSaveMessage('Rule added.');
  }

  removeGlobalRulePlaceholder(ruleIndex: number): void {
    const sheetName = this.activeSheetName();
    const current = this.globalRulePlaceholders();
    const rules = [...(current[sheetName] || [])];
    if (!rules[ruleIndex]) return;
    rules.splice(ruleIndex, 1);
    this.globalRulePlaceholders.set({ ...current, [sheetName]: rules });
    this.setSaveMessage('Rule removed.');
  }

  updateGlobalRulePlaceholder<K extends keyof RulePlaceholder>(
    ruleIndex: number,
    key: K,
    value: RulePlaceholder[K],
  ): void {
    const sheetName = this.activeSheetName();
    const current = this.globalRulePlaceholders();
    const rules = [...(current[sheetName] || [])];
    if (!rules[ruleIndex]) return;
    rules[ruleIndex] = { ...rules[ruleIndex], [key]: value };
    this.globalRulePlaceholders.set({ ...current, [sheetName]: rules });
  }

  removeStatusField(index: number): void {
    const deam = this.cloneDeamAnalysis();
    const sheet = this.findMutableDeamSheet(deam);
    if (!sheet?.statusFields[index]) return;
    sheet.statusFields.splice(index, 1);
    this.deamAnalysis.set(deam);
    this.setSaveMessage('Status field removed.');
  }

  addStatusTransition(statusIndex: number): void {
    const deam = this.cloneDeamAnalysis();
    const sheet = this.findMutableDeamSheet(deam);
    const status = sheet?.statusFields[statusIndex];
    if (!status) return;
    const fromStatus = status.allowedValues[0] || '';
    status.transitions.push({
      fromStatus,
      toStatuses: status.allowedValues.filter((v) => v !== fromStatus),
    });
    this.deamAnalysis.set(deam);
    this.setSaveMessage('Transition added.');
  }

  removeStatusTransition(statusIndex: number, transitionIndex: number): void {
    const deam = this.cloneDeamAnalysis();
    const sheet = this.findMutableDeamSheet(deam);
    const status = sheet?.statusFields[statusIndex];
    if (!status?.transitions[transitionIndex]) return;
    status.transitions.splice(transitionIndex, 1);
    this.deamAnalysis.set(deam);
    this.setSaveMessage('Transition removed.');
  }

  updateStatusTransitionFrom(statusIndex: number, transitionIndex: number, value: string): void {
    const deam = this.cloneDeamAnalysis();
    const sheet = this.findMutableDeamSheet(deam);
    const transition = sheet?.statusFields[statusIndex]?.transitions[transitionIndex];
    if (!transition) return;
    transition.fromStatus = value.trim();
    this.deamAnalysis.set(deam);
  }

  updateStatusTransitionTargets(statusIndex: number, transitionIndex: number, rawValue: string): void {
    const deam = this.cloneDeamAnalysis();
    const sheet = this.findMutableDeamSheet(deam);
    const transition = sheet?.statusFields[statusIndex]?.transitions[transitionIndex];
    if (!transition) return;
    transition.toStatuses = this.parseCsvValues(rawValue);
    this.deamAnalysis.set(deam);
  }

  addStatusRulePlaceholder(statusIndex: number): void {
    const deam = this.cloneDeamAnalysis();
    const sheet = this.findMutableDeamSheet(deam);
    const status = sheet?.statusFields[statusIndex];
    if (!status) return;
    const fromStatus = status.defaultValue || status.allowedValues[0] || '';
    const toStatus = status.allowedValues.find((v) => v !== fromStatus) || '';
    status.rulePlaceholders = status.rulePlaceholders || [];
    status.rulePlaceholders.push({
      event: 'on_status_change',
      fromStatus,
      toStatus,
      name: `Rule for ${status.fieldName}`,
      description: '',
      expression: '',
      enabled: false,
    });
    this.deamAnalysis.set(deam);
    this.setSaveMessage('Status rule added.');
  }

  removeStatusRulePlaceholder(statusIndex: number, placeholderIndex: number): void {
    const deam = this.cloneDeamAnalysis();
    const sheet = this.findMutableDeamSheet(deam);
    const placeholders = sheet?.statusFields[statusIndex]?.rulePlaceholders;
    if (!placeholders?.[placeholderIndex]) return;
    placeholders.splice(placeholderIndex, 1);
    this.deamAnalysis.set(deam);
    this.setSaveMessage('Status rule removed.');
  }

  updateStatusRulePlaceholder<K extends keyof StatusRulePlaceholder>(
    statusIndex: number,
    placeholderIndex: number,
    key: K,
    value: StatusRulePlaceholder[K],
  ): void {
    const deam = this.cloneDeamAnalysis();
    const sheet = this.findMutableDeamSheet(deam);
    const placeholder = sheet?.statusFields[statusIndex]?.rulePlaceholders?.[placeholderIndex];
    if (!placeholder) return;
    placeholder[key] = value;
    this.deamAnalysis.set(deam);
  }

  addManualStatusField(): void {
    const deam = this.cloneDeamAnalysis();
    const sheet = this.findMutableDeamSheet(deam);
    if (!sheet) return;
    const values = ['Pending', 'Approved', 'Rejected'];
    sheet.statusFields.push({
      sourceSheet: sheet.sheetName,
      sourceColumn: 'manual',
      sourceHeader: 'Manual Status',
      fieldName: 'manual_status',
      statusLocation: {
        sheet: sheet.sheetName,
        column: 'manual',
        header: 'Manual Status',
        deamColumnName: 'manual_status',
      },
      included: true,
      confidence: 1,
      detectionReasons: ['Added manually by user'],
      allowedValues: values,
      defaultValue: values[0],
      transitions: this.buildTransitions(values),
      rulePlaceholders: [],
    });
    this.deamAnalysis.set(deam);
    this.setSaveMessage('Status field added.');
  }

  // ── Save / load / download ────────────────────────────────────────────────

  saveDeamDraft(message = 'Draft saved in this browser.'): void {
    const deam = this.deamAnalysis();
    if (!deam) {
      this.setErrorMessage('Analyze or load a DEAM schema before saving a draft.');
      return;
    }
    localStorage.setItem(
      'spriced-lite-deam-draft',
      JSON.stringify({
        activeSheetName: this.activeSheetName(),
        deamAnalysis: deam,
        globalRulePlaceholders: this.globalRulePlaceholders(),
      }),
    );
    this.setSaveMessage(message);
  }

  loadDeamDraft(): void {
    const rawDraft = localStorage.getItem('spriced-lite-deam-draft');
    if (!rawDraft) {
      this.setErrorMessage('No draft found in this browser.');
      return;
    }
    try {
      const draft = JSON.parse(rawDraft) as {
        activeSheetName?: string;
        deamAnalysis?: DeamAnalysisResponse;
        globalRulePlaceholders?: Record<string, RulePlaceholder[]>;
      };
      if (!draft.deamAnalysis) {
        this.setErrorMessage('Saved draft is invalid.');
        return;
      }
      this.deamAnalysis.set(draft.deamAnalysis);
      this.globalRulePlaceholders.set(
        draft.globalRulePlaceholders || this.initializeGlobalRulePlaceholders(draft.deamAnalysis),
      );
      if (draft.activeSheetName) this.activeSheetName.set(draft.activeSheetName);
      this.setSaveMessage('Draft loaded from this browser.');
    } catch {
      this.setErrorMessage('Saved draft could not be loaded.');
    }
  }

  downloadDeamSchema(): void {
    const content = this.finalDeamSchemaJson();
    if (!content) {
      this.setErrorMessage('Analyze or load a DEAM schema before downloading JSON.');
      return;
    }
    const sheet = this.activeDeamSheet();
    const fileName = sheet
      ? `${this.toSnakeCase(sheet.sheetName)}_deam_schema.json`
      : 'deam_schema.json';
    this.downloadJsonContent(content, fileName, 'Schema JSON downloaded.');
  }

  downloadFinalDataExport(): void {
    const finalSchema = this.parseJsonContent(this.finalDeamSchemaJson());
    if (!finalSchema) {
      this.setErrorMessage('Analyze or load a DEAM schema before exporting final data.');
      return;
    }
    this.translationAction.emit(this.buildTranslationActionEvent('export'));

    const joinedPlans = this.joinedFilePlans();
    const definitions = Array.isArray(finalSchema) ? finalSchema : [finalSchema];
    const joinedDefinition = definitions.find((entry) => {
      if (!entry || typeof entry !== 'object') return false;
      const sourceFiles = (entry as Record<string, unknown>)['sourceFiles'];
      return Array.isArray(sourceFiles) && sourceFiles.length > 1;
    }) || null;
    const activeSheet = this.activeDeamSheet();
    const joinKey = joinedPlans.map((p) => p.joinKey.trim()).find(Boolean) || this.primaryKey().trim() || 'uuid';
    const exportName = this.entityName().trim() || this.activeSheetName() || this.activeFileName() || 'final_data';

    this.downloadJsonPayload(
      {
        exportType: 'final-data',
        generatedAt: new Date().toISOString(),
        activeFileName: this.activeFileName(),
        activeSheetName: this.activeSheetName(),
        workflowName: this.workflowName().trim() || this.entityName().trim() || this.deamPreviewTitle(),
        joinKey,
        filePlans: this.filePlans(),
        joinedData: {
          enabled: joinedPlans.length > 1,
          files: joinedPlans,
          definition: joinedDefinition,
        },
        preparedData: {
          workflowConfig: this.parseJsonContent(this.workflowPreviewJson()) || this.workflowPreviewJson(),
          deamDefinitionJson: finalSchema,
          activeSheetSchema: activeSheet || null,
          validation: this.schemaValidation(),
        },
      },
      `${this.toSnakeCase(exportName)}_final_data.json`,
      'Final data export downloaded.',
    );
  }
  downloadJoinedDataExport(): void {
    const joinedPlans = this.joinedFilePlans();
    if (joinedPlans.length < 2) {
      this.setErrorMessage('Select at least two files as Join before exporting joined data.');
      return;
    }

    const finalSchema = this.parseJsonContent(this.finalDeamSchemaJson());
    if (!finalSchema) {
      this.setErrorMessage('Analyze or load a DEAM schema before exporting joined data.');
      return;
    }

    const definitions = Array.isArray(finalSchema) ? finalSchema : [finalSchema];
    const joinedDefinition = definitions.find((entry) => {
      if (!entry || typeof entry !== 'object') return false;
      const sourceFiles = (entry as Record<string, unknown>)['sourceFiles'];
      return Array.isArray(sourceFiles) && sourceFiles.length > 1;
    }) || definitions[0];

    const joinKey = joinedPlans.map((p) => p.joinKey.trim()).find(Boolean) || this.primaryKey().trim() || 'uuid';
    this.downloadJsonPayload(
      {
        exportType: 'joined-data',
        generatedAt: new Date().toISOString(),
        joinKey,
        files: joinedPlans,
        workflowName: this.workflowName().trim() || this.entityName().trim() || this.deamPreviewTitle(),
        joinedDefinition,
      },
      `${this.toSnakeCase(this.entityName().trim() || joinedPlans[0].entityName || 'joined_data')}_joined_data.json`,
      'Joined data export downloaded.',
    );
  }

  downloadPreparedDataExport(): void {
    const finalSchema = this.parseJsonContent(this.finalDeamSchemaJson());
    if (!finalSchema) {
      this.setErrorMessage('Analyze or load a DEAM schema before exporting prepared data.');
      return;
    }

    const activeSheet = this.activeDeamSheet();
    this.downloadJsonPayload(
      {
        exportType: 'prepared-data',
        generatedAt: new Date().toISOString(),
        activeFileName: this.activeFileName(),
        activeSheetName: this.activeSheetName(),
        filePlans: this.filePlans(),
        workflowConfig: this.parseJsonContent(this.workflowPreviewJson()) || this.workflowPreviewJson(),
        deamDefinitionJson: finalSchema,
        activeSheetSchema: activeSheet || null,
        validation: this.schemaValidation(),
      },
      `${this.toSnakeCase(this.activeSheetName() || this.activeFileName() || 'prepared_data')}_prepared_data.json`,
      'Prepared data export downloaded.',
    );
  }

  private downloadJsonPayload(payload: unknown, fileName: string, message: string): void {
    this.downloadJsonContent(JSON.stringify(payload, null, 2), fileName, message);
  }

  private downloadJsonContent(content: string, fileName: string, message: string): void {
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
    this.setSaveMessage(message);
  }

  private parseJsonContent(content: string): unknown | null {
    if (!content.trim()) return null;
    try {
      return JSON.parse(content) as unknown;
    } catch {
      return null;
    }
  }

  persistDeamSchema(): void {
    const workflowStorePayload = this.buildWorkflowStoreUpdatePayload();
    const entityStorePayload = this.buildEntityStoreUpdatePayload();
    const changeCount = this.buildWorkflowColumnUpdateOperations().length;

    this.persisting.set(true);
    this.error.set('');
    this.saveMessage.set('');

    this.apiService.updateWorkflowStore(workflowStorePayload).pipe(
      switchMap(() => this.apiService.updateWorkflowStore(entityStorePayload)),
    ).subscribe({
      next: () => {
        this.persisting.set(false);
        this.setSaveMessage('Workflow and entity store updated successfully.');
        this.translationAction.emit(this.buildTranslationActionEvent('save', changeCount));
      },
      error: (err) => {
        this.persisting.set(false);
        this.setErrorMessage(this.describeHttpError(err, 'Workflow or entity store update failed.'));
      },
    });
  }

  private persistReviewedDeamSchema(files: File[]): void {
    this.translationAction.emit(this.buildTranslationActionEvent('save'));

    const preview = this.workflowPreview();

    if (preview?.workflowConfig) {
      const entity = this.entityName().trim() || this.deamPreviewTitle();
      const workflow = this.workflowName().trim() || entity;
      const pk = preview.primaryKey || this.primaryKey().trim() || 'uuid';
      const filePlanJson = this.filePlans().length ? JSON.stringify(this.filePlans()) : null;

      this.persisting.set(true);
      this.error.set('');
      this.saveMessage.set('');
      this.ingestionJobId.set(null);
      this.ingestionProgress.set(null);
      this.ingestionEstimate.set('');

      this.apiService.persistWorkflowAsync({
        files,
        workflowName: workflow,
        entityName: entity,
        primaryKey: pk,
        userId: this.getUserId(),
        workflowConfig: this.workflowPreviewJson(),
        deamDefinitionJson: this.finalDeamSchemaJson(),
        sheetName: this.sheetName().trim() || null,
        filePlanJson,
      }).subscribe({
        next: (jobInfo) => {
          const jobId = jobInfo['jobId'] as string;
          const estimate = jobInfo['message'] as string;
          this.ingestionJobId.set(jobId);
          this.ingestionEstimate.set(estimate);
          this.setSaveMessage('Ingestion started.', false);
          this.startPolling(jobId, workflow, entity);
        },
        error: (err) => {
          this.persisting.set(false);
          this.setErrorMessage(this.describeHttpError(err, 'Workflow persistence failed.'));
        },
      });
    } else {
      const file = files[0];
      this.persisting.set(true);
      this.error.set('');
      this.saveMessage.set('');
      this.apiService.persistDeamDefinition(file, this.finalDeamSchemaJson(), this.activeSheetName() || null)
        .subscribe({
          next: (response) => {
            this.persisting.set(false);
            this.setSaveMessage(
              `Persisted ${response['tableName'] || 'DEAM table'} with ${response['rowsInserted'] || 0} rows.`,
            );
            this.viewModeChange.emit('data');
          },
          error: (err) => {
            this.persisting.set(false);
            this.setErrorMessage(this.describeHttpError(err, 'DEAM persistence failed.'));
          },
      });
    }
  }

  private buildTranslationActionEvent(action: 'export' | 'save', changeCount = this.buildColumnUpdateOperations().length): TranslationActionEvent {
    const columns = (this.activeDeamSheet()?.columns ?? [])
      .filter((column) => column.included !== false)
      .map((column) => ({
        sourceColumn: column.sourceColumn,
        sourceHeader: column.sourceHeader,
        columnName: column.deamColumnName,
        dataType: column.dataType,
      }));

    return {
      action,
      columns,
      changeCount,
    };
  }

  private buildColumnUpdateOperations(): ColumnUpdateOperation[] {
    const basePayload = this.resolveValidateAndSavePayload(this.workflowSourceDeamResponse());
    const activeSheet = this.activeDeamSheet();
    if (!basePayload || !activeSheet) return [];

    const deam = basePayload['deam'];
    if (!deam || typeof deam !== 'object') return [];

    const attributes = (deam as Record<string, unknown>)['attributesList'];
    if (!Array.isArray(attributes)) return [];

    const editableColumnKeys = this.getInitialDeamColumnKeys();
    const currentColumnsByKey = new Map<string, DeamColumnRecommendation>();
    for (const column of activeSheet.columns.filter((c) => c.included !== false)) {
      const key = this.deamAttributeKeyForColumn(column);
      if (key) currentColumnsByKey.set(key, column);
    }

    const operations: ColumnUpdateOperation[] = [];
    for (const attribute of attributes) {
      if (!attribute || typeof attribute !== 'object' || Array.isArray(attribute)) continue;

      const attributeRecord = attribute as Record<string, unknown>;
      const originalName = String(attributeRecord['name'] || '').trim();
      const originalKey = this.normalizeDeamAttributeKey(originalName);
      if (!originalName || !editableColumnKeys.has(originalKey)) continue;

      const matchingColumn = currentColumnsByKey.get(originalKey);
      if (!matchingColumn) {
        operations.push({ operation: 'DROP_COLUMN', columnName: originalName });
        continue;
      }

      const nextName = matchingColumn.deamColumnName.trim();
      if (nextName && nextName !== originalName) {
        operations.push({
          operation: 'RENAME_COLUMN',
          columnName: originalName,
          newColumnName: nextName,
        });
      }

      const originalType = String(attributeRecord['type'] || '').trim().toLowerCase();
      const nextType = this.toDeamType(matchingColumn.dataType);
      if (nextType && nextType !== originalType) {
        operations.push({
          operation: 'CHANGE_DATA_TYPE',
          columnName: nextName || originalName,
          newDataType: nextType,
        });
      }
    }

    return operations;
  }

  private buildWorkflowStoreUpdatePayload(): Record<string, unknown> {
    const operations = this.buildWorkflowColumnUpdateOperations();
    const workflowStoreRow = this.resolveWorkflowStoreRow(this.datasetService.getLatestWorkflowStoreResponse());
    const workflows = this.parseWorkflowStoreWorkflows(workflowStoreRow?.['workflows']) || {};

    const modifiedWorkflows = operations.reduce<unknown>(
      (currentWorkflows, operation) => this.applyWorkflowColumnOperation(currentWorkflows, operation),
      this.cloneUnknown(workflows),
    );
    const workflowRenameMap = this.buildWorkflowColumnRenameMap();
    const renamedWorkflows = workflowRenameMap.size
      ? this.applyWorkflowMapToRenames(modifiedWorkflows, workflowRenameMap)
      : modifiedWorkflows;
    const rewrittenWorkflows = this.applyWorkflowTargetMetadataColumns(renamedWorkflows);
    const workflowId = String(
      workflowStoreRow?.['workflow_id']
      || (rewrittenWorkflows as Record<string, unknown>)?.['workflowId']
      || this.entityName()
      || 'ABC-123',
    );
    const uuid = String(workflowStoreRow?.['uuid'] || '2e384f87-e58c-4be3-a0f0-f9cee982a791').trim();

    return {
      requestData: {
        workflows: rewrittenWorkflows,
        uuid,
      },
      operation: 'update',
      responseList: [],
      entity: 'workflow_store',
      batchId: 'test',
      workflowId,
      endpoints: [],
      version: '1.0',
    };
  }

  private buildEntityStoreUpdatePayload(): Record<string, unknown> {
    const payload = this.buildEntityStoreRequestDataPayload();
    const workflowStoreRow = this.resolveWorkflowStoreRow(this.datasetService.getLatestWorkflowStoreResponse());
    const workflowId = String(
      workflowStoreRow?.['workflow_id']
      || this.entityName()
      || 'ABC-123',
    );
    const uuid = this.resolveEntityStoreUuid() || '2e384f87-e58c-4be3-a0f0-f9cee982a791';

    return {
      requestData: {
        payload,
        uuid,
      },
      operation: 'update',
      responseList: [],
      entity: 'entity_store',
      batchId: 'test',
      workflowId,
      endpoints: [],
      version: '1.0',
    };
  }

  private buildEntityStoreRequestDataPayload(): Record<string, unknown> {
    const payload = this.resolveValidateAndSavePayload(this.workflowSourceDeamResponse());
    const activeSheet = this.activeDeamSheet();
    if (!payload || !activeSheet) return {};

    const deam = payload['deam'];
    if (!deam || typeof deam !== 'object') return payload;

    const deamRecord = deam as Record<string, unknown>;
    const attributes = deamRecord['attributesList'];
    if (!Array.isArray(attributes)) return payload;

    const editableColumnKeys = this.getInitialDeamColumnKeys();
    const currentColumnsByKey = new Map<string, DeamColumnRecommendation>();
    for (const column of activeSheet.columns.filter((c) => c.included !== false)) {
      const key = this.deamAttributeKeyForColumn(column);
      if (key) currentColumnsByKey.set(key, column);
    }

    deamRecord['attributesList'] = attributes.flatMap((attribute) => {
      if (!attribute || typeof attribute !== 'object' || Array.isArray(attribute)) {
        return [attribute];
      }

      const attributeRecord = attribute as Record<string, unknown>;
      const attributeName = this.normalizeDeamAttributeKey(attributeRecord['name']);
      if (!attributeName || !editableColumnKeys.has(attributeName)) {
        return [attribute];
      }

      const matchingColumn = currentColumnsByKey.get(attributeName);
      if (!matchingColumn) {
        return [];
      }

      attributeRecord['name'] = matchingColumn.deamColumnName.trim();
      attributeRecord['type'] = this.toDeamType(matchingColumn.dataType);
      return [attributeRecord];
    });

    if (!payload['entityName'] && typeof deamRecord['entity'] === 'string') {
      payload['entityName'] = deamRecord['entity'];
    }

    return payload;
  }

  private resolveEntityStoreUuid(): string {
    return this.findStringByKey(this.workflowSourceDeamResponse(), 'uuid');
  }

  private findStringByKey(value: unknown, targetKey: string): string {
    if (!value) return '';
    if (Array.isArray(value)) {
      for (const item of value) {
        const match = this.findStringByKey(item, targetKey);
        if (match) return match;
      }
      return '';
    }
    if (typeof value !== 'object') return '';

    const record = value as Record<string, unknown>;
    const direct = record[targetKey];
    if (typeof direct === 'string' && direct.trim()) return direct.trim();

    for (const nested of Object.values(record)) {
      const match = this.findStringByKey(nested, targetKey);
      if (match) return match;
    }
    return '';
  }

  private buildWorkflowColumnUpdateOperations(): ColumnUpdateOperation[] {
    const operations = this.buildColumnUpdateOperations();
    const activeSheet = this.activeDeamSheet();
    if (!activeSheet) return operations;

    const operationKey = (operation: ColumnUpdateOperation): string => (
      operation.operation === 'RENAME_COLUMN'
        ? `${operation.operation}:${operation.columnName}:${operation.newColumnName}`
        : operation.operation === 'CHANGE_DATA_TYPE'
          ? `${operation.operation}:${operation.columnName}:${operation.newDataType}`
          : `${operation.operation}:${operation.columnName}`
    );
    const seen = new Set(operations.map(operationKey));

    for (const column of activeSheet.columns) {
      const originalName = String(
        column.originalDeamColumnName
        || this.toSnakeCase(column.sourceHeader)
        || '',
      ).trim();
      const nextName = String(column.deamColumnName || '').trim();
      if (!originalName) continue;

      const operation = column.included === false
        ? { operation: 'DROP_COLUMN', columnName: originalName } as ColumnUpdateOperation
        : nextName && nextName !== originalName
          ? { operation: 'RENAME_COLUMN', columnName: originalName, newColumnName: nextName } as ColumnUpdateOperation
          : null;
      if (!operation) continue;

      const key = operationKey(operation);
      if (!seen.has(key)) {
        operations.push(operation);
        seen.add(key);
      }
    }

    return operations;
  }

  private buildWorkflowColumnRenameMap(): Map<string, string> {
    const renameMap = new Map<string, string>();
    const activeSheet = this.activeDeamSheet();
    if (!activeSheet) return renameMap;

    for (const column of activeSheet.columns) {
      if (column.included === false) continue;

      const originalName = String(
        column.originalDeamColumnName
        || this.toSnakeCase(column.sourceHeader)
        || '',
      ).trim();
      const nextName = String(column.deamColumnName || '').trim();

      if (originalName && nextName && originalName !== nextName) {
        renameMap.set(originalName, nextName);
      }
    }

    return renameMap;
  }

  private resolveWorkflowStoreRow(response: unknown): Record<string, unknown> | null {
    if (!response) return null;
    if (Array.isArray(response)) {
      for (const item of response) {
        const match = this.resolveWorkflowStoreRow(item);
        if (match) return match;
      }
      return null;
    }
    if (typeof response !== 'object') return null;

    const record = response as Record<string, unknown>;
    if (this.parseWorkflowStoreWorkflows(record['workflows'])) return record;
    for (const key of ['data', 'result', 'response', 'body', 'items']) {
      const match = this.resolveWorkflowStoreRow(record[key]);
      if (match) return match;
    }
    return null;
  }

  private parseWorkflowStoreWorkflows(value: unknown): Record<string, unknown> | null {
    if (!value) return null;
    if (typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
    if (typeof value !== 'string') return null;

    const trimmed = value.trim();
    if (!trimmed) return null;

    try {
      const parsed = JSON.parse(trimmed) as unknown;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed as Record<string, unknown>
        : null;
    } catch {
      return null;
    }
  }

  private applyWorkflowColumnOperation(value: unknown, operation: ColumnUpdateOperation): unknown {
    if (typeof value === 'string') {
      if (operation.operation === 'RENAME_COLUMN' && value === operation.columnName) {
        return operation.newColumnName;
      }
      if (operation.operation === 'DROP_COLUMN' && value === operation.columnName) {
        return undefined;
      }
      return value;
    }

    if (Array.isArray(value)) {
      return value
        .map((item) => this.applyWorkflowColumnOperation(item, operation))
        .filter((item) => item !== undefined);
    }

    if (!value || typeof value !== 'object') return value;

    const record = value as Record<string, unknown>;
    if (operation.operation === 'DROP_COLUMN' && this.shouldDropWorkflowObject(record, operation.columnName)) {
      return undefined;
    }

    const nextRecord: Record<string, unknown> = {};
    for (const [key, childValue] of Object.entries(record)) {
      if (operation.operation === 'DROP_COLUMN' && key === operation.columnName) continue;
      const nextKey = operation.operation === 'RENAME_COLUMN' && key === operation.columnName
        ? operation.newColumnName
        : key;
      const nextValue = this.applyWorkflowColumnOperation(childValue, operation);
      if (nextValue !== undefined) {
        nextRecord[nextKey] = nextValue;
      }
    }

    if (operation.operation === 'CHANGE_DATA_TYPE' && this.workflowObjectReferencesColumn(nextRecord, operation.columnName)) {
      nextRecord['dataType'] = operation.newDataType;
    }

    return nextRecord;
  }

  private applyWorkflowMapToRenames(value: unknown, renameMap: Map<string, string>): unknown {
    if (Array.isArray(value)) {
      return value.map((item) => this.applyWorkflowMapToRenames(item, renameMap));
    }

    if (!value || typeof value !== 'object') return value;

    const record = value as Record<string, unknown>;
    const nextRecord: Record<string, unknown> = {};
    for (const [key, childValue] of Object.entries(record)) {
      if (key === 'mapTo' && Array.isArray(childValue)) {
        nextRecord[key] = childValue.map((item) => (
          typeof item === 'string' && renameMap.has(item) ? renameMap.get(item) : item
        ));
      } else {
        nextRecord[key] = this.applyWorkflowMapToRenames(childValue, renameMap);
      }
    }
    return nextRecord;
  }

  private applyWorkflowTargetMetadataColumns(value: unknown): unknown {
    const activeColumns = (this.activeDeamSheet()?.columns ?? []).filter((column) => column.included !== false);
    if (!activeColumns.length) return value;
    return this.rewriteTargetMetadataByColumnOrder(value, activeColumns);
  }

  private rewriteTargetMetadataByColumnOrder(value: unknown, columns: DeamColumnRecommendation[]): unknown {
    if (Array.isArray(value)) {
      return value.map((item) => this.rewriteTargetMetadataByColumnOrder(item, columns));
    }

    if (!value || typeof value !== 'object') return value;

    const record = value as Record<string, unknown>;
    const nextRecord: Record<string, unknown> = {};
    for (const [key, childValue] of Object.entries(record)) {
      if (key === 'targetMetadata' && Array.isArray(childValue)) {
        nextRecord[key] = childValue.map((item, index) => {
          if (!item || typeof item !== 'object' || Array.isArray(item)) return item;

          const column = columns[index];
          const nextColumnName = String(column?.deamColumnName || '').trim();
          if (!nextColumnName) return item;

          return {
            ...(item as Record<string, unknown>),
            mapTo: [nextColumnName],
          };
        });
      } else {
        nextRecord[key] = this.rewriteTargetMetadataByColumnOrder(childValue, columns);
      }
    }
    return nextRecord;
  }

  private shouldDropWorkflowObject(record: Record<string, unknown>, columnName: string): boolean {
    if (Array.isArray(record['mapTo']) && record['mapTo'].includes(columnName)) return true;
    const keys = Object.keys(record);
    return keys.length === 1 && keys[0] === columnName;
  }

  private workflowObjectReferencesColumn(record: Record<string, unknown>, columnName: string): boolean {
    if (Array.isArray(record['mapTo']) && record['mapTo'].includes(columnName)) return true;
    if (record[columnName] && typeof record[columnName] === 'object') return true;
    return false;
  }

  private resolveValidateAndSavePayload(response: unknown): Record<string, unknown> | null {
    const cloned = this.cloneUnknown(response);
    return this.findValidateAndSavePayload(cloned);
  }

  private findValidateAndSavePayload(value: unknown): Record<string, unknown> | null {
    if (!value) return null;
    if (Array.isArray(value)) {
      for (const item of value) {
        const match = this.findValidateAndSavePayload(item);
        if (match) return match;
      }
      return null;
    }
    if (typeof value !== 'object') return null;

    const record = value as Record<string, unknown>;
    if (record['deam'] && typeof record['deam'] === 'object') return record;

    for (const key of ['deams', 'data', 'result', 'response', 'body']) {
      const match = this.findValidateAndSavePayload(record[key]);
      if (match) return match;
    }
    return null;
  }

  private getInitialDeamColumnKeys(): Set<string> {
    const fileName = this.activeFileName();
    const existing = fileName ? this.initialDeamColumnKeysByFile.get(fileName) : null;
    if (existing?.size) return existing;

    const activeSheet = this.activeDeamSheet();
    const fallback = new Set<string>();
    for (const column of activeSheet?.columns ?? []) {
      const key = this.deamAttributeKeyForColumn(column);
      if (key) fallback.add(key);
    }
    return fallback;
  }

  private captureInitialDeamColumnKeys(fileName: string, deam: DeamAnalysisResponse | null | undefined): void {
    if (!fileName || !deam || this.initialDeamColumnKeysByFile.has(fileName)) return;
    const sheet = deam.sheetSchemas.find((s) => s.sheetName === deam.selectedSheet) || deam.sheetSchemas[0] || null;
    const keys = new Set<string>();
    for (const column of sheet?.columns ?? []) {
      const key = this.deamAttributeKeyForColumn(column);
      if (key) keys.add(key);
    }
    this.initialDeamColumnKeysByFile.set(fileName, keys);
  }

  private deamAttributeKeyForColumn(column: DeamColumnRecommendation): string {
    return (
      this.normalizeDeamAttributeKey(column.originalDeamColumnName)
      || this.normalizeDeamAttributeKey(column.deamColumnName)
      || this.normalizeDeamAttributeKey(this.toSnakeCase(column.sourceHeader))
    );
  }

  private normalizeDeamAttributeKey(value: unknown): string {
    return String(value ?? '').trim().toLowerCase();
  }

  private formatLocalIsoDateTime(date: Date): string {
    const pad = (value: number, length = 2): string => String(value).padStart(length, '0');
    const offsetMinutes = -date.getTimezoneOffset();
    const offsetSign = offsetMinutes >= 0 ? '+' : '-';
    const absoluteOffsetMinutes = Math.abs(offsetMinutes);
    const offsetHours = Math.floor(absoluteOffsetMinutes / 60);
    const offsetRemainderMinutes = absoluteOffsetMinutes % 60;

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
      + `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
      + `.${pad(date.getMilliseconds(), 3)}`
      + `${offsetSign}${pad(offsetHours)}:${pad(offsetRemainderMinutes)}`;
  }

  private cloneUnknown<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
  }

  private startPolling(jobId: string, workflow: string, entity: string): void {
    this.pollSub?.unsubscribe();
    this.pollSub = interval(10000)
      .pipe(
        switchMap(() => this.apiService.getIngestionStatus(jobId)),
        takeWhile((status) => status['status'] !== 'COMPLETED' && status['status'] !== 'FAILED', true),
      )
      .subscribe({
        next: (status) => {
          this.ingestionProgress.set(status);
          const pct = status['percentComplete'] as number ?? 0;
          const processed = status['processedRows'] as number ?? 0;
          const total = status['totalRows'] as number ?? 0;
          const batch = status['completedBatches'] as number ?? 0;
          const totalBatches = status['totalBatches'] as number ?? 0;
          if (status['status'] === 'COMPLETED') {
            this.persisting.set(false);
            this.setSaveMessage(
              `Saved Successfully`,
            );
            this.ingestionJobId.set(null);
            this.ingestionEstimate.set('');
            this.viewModeChange.emit('data');
          } else if (status['status'] === 'FAILED') {
            this.persisting.set(false);
            this.setErrorMessage(`Ingestion failed: ${status['errorMessage'] || 'Unknown error'}`);
            this.ingestionJobId.set(null);
            this.ingestionEstimate.set('');
          } else {
            const progressMsg = totalBatches > 0
              ? `Processing batch ${batch}/${totalBatches} · ${processed.toLocaleString()}/${total.toLocaleString()} rows (${pct}%)`
              : `Ingesting data… ${processed.toLocaleString()} rows processed`;
            this.setSaveMessage(progressMsg, false);
          }
        },
        error: () => {
          // polling network error — stop and let user know
          this.persisting.set(false);
          this.setErrorMessage('Lost contact with ingestion job. Check server logs.');
        },
      });
  }

  // ── Rule execution ────────────────────────────────────────────────────────

  executeSelectedRule(): void {
    const rule = this.selectedRule();
    if (!rule) return;
    this.executeRules([rule], 'selected');
  }

  executeAllRules(): void {
    const sheet = this.activeSheet();
    if (!sheet?.rules.length) return;
    this.executeRules(sheet.rules, 'all');
  }

  private executeRules(rules: Rule[], mode: 'selected' | 'all'): void {
    let input: Record<string, unknown>;
    try {
      input = JSON.parse(this.inputJson());
    } catch {
      this.setErrorMessage('Input JSON is invalid.');
      return;
    }
    this.executing.set(true);
    this.runMode.set(mode);
    this.error.set('');
    this.apiService.executeRules({ input, rules }).subscribe({
      next: (response) => {
        this.outputJson.set(JSON.stringify(response, null, 2));
        this.executing.set(false);
        this.setSaveMessage(mode === 'all' ? 'All rules executed.' : 'Selected rule executed.');
      },
      error: (err) => {
        this.setErrorMessage(this.describeHttpError(err, 'Rule execution failed.'));
        this.executing.set(false);
      },
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private describeHttpError(error: unknown, fallback: string): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) {
        return `${fallback}: backend is not reachable or CORS blocked the request. Check ${this.apiService.apiBaseUrl}.`;
      }
      const detail =
        typeof error.error?.detail === 'string'
          ? error.error.detail
          : typeof error.error === 'string'
            ? error.error
            : error.message;
      return `${fallback}: ${detail || `HTTP ${error.status}`}`;
    }
    return fallback;
  }

  private buildSampleInput(columns: ExcelColumn[]): string {
    const input: Record<string, unknown> = {};
    for (const col of columns) {
      if (col.dataType === 'unknown') continue;
      input[col.fieldName] = col.sampleValue ?? this.defaultValueForType(col.dataType);
    }
    return JSON.stringify(input, null, 2);
  }

  private defaultValueForType(dataType: string): unknown {
    if (dataType === 'integer' || dataType === 'number') return 0;
    if (dataType === 'boolean') return false;
    return '';
  }

  formatStatusLocation(status: DeamStatusFieldRecommendation): string {
    const sheet = this.activeDeamSheet();
    const location = sheet
      ? this.resolveStatusLocation(sheet, status)
      : status.statusLocation || {
          sheet: status.sourceSheet,
          column: status.sourceColumn,
          header: status.sourceHeader,
          deamColumnName: status.fieldName,
        };
    return `${location.sheet} / ${location.column} / ${location.header} / ${location.deamColumnName}`;
  }

  private buildFinalDeamDefinitions(deam: DeamAnalysisResponse): Record<string, unknown>[] {
    return deam.sheetSchemas.map((sheet) => this.buildDeamEntity(sheet));
  }

  private buildDeamEntity(sheet: DeamSheetSchemaRecommendation, sourceFileName?: string): Record<string, unknown> {
    const entityName = this.toSnakeCase(sheet.sheetName);
    const attributes = this.buildDeamAttributes(sheet, sheet.sheetName);
    return {
      entity: entityName,
      deamReads: this.buildDeamReads(entityName, attributes),
      primaryKey: 'uuid',
      displayName: this.toDisplayName(sheet.sheetName),
      businessRules: this.buildDeamBusinessRules(sheet),
      childEntities: [],
      attributesList: attributes,
      primaryKeyType: 'string',
      sheetName: sheet.sheetName,
      sourceFiles: sourceFileName ? [sourceFileName] : [],
      sourceSheets: [sheet.sheetName],
    };
  }

  private buildMergedDeamEntity(
    joinedPlans: FilePlan[],
    deamAnalyses: DeamAnalysisResponse[],
  ): Record<string, unknown> {
    const primaryPlan = joinedPlans[0];
    const entityName = this.toSnakeCase(primaryPlan.entityName || primaryPlan.fileName.replace(/\.[^.]+$/, ''));
    const sourceFiles = joinedPlans.map((p) => p.fileName);
    const sourceSheets: string[] = [];

    const attrMap = new Map<string, Record<string, unknown>>();

    attrMap.set('id', {
      name: 'id', type: 'long', scale: null, unique: false, indexed: false, mandatory: true,
      maxLength: null, precision: null, businessKey: false, displayName: 'ID',
      validValues: [], staticString: '', autogenerated: true, autoGenerated: true, enableAttribute: true,
    });
    attrMap.set('uuid', {
      name: 'uuid', type: 'string', scale: null, unique: true, indexed: true, mandatory: true,
      maxLength: 255, precision: null, businessKey: false, displayName: 'uuid',
      validValues: [], staticString: '', autogenerated: true, autoGenerated: true, enableAttribute: true,
    });

    for (let i = 0; i < joinedPlans.length; i++) {
      const plan = joinedPlans[i];
      const deam = deamAnalyses[i];
      if (!deam) continue;
      const sheetName = plan.sheetName.trim() || deam.selectedSheet;
      if (sheetName && !sourceSheets.includes(sheetName)) sourceSheets.push(sheetName);
      const sheet = deam.sheetSchemas.find((s) => s.sheetName === sheetName) || deam.sheetSchemas[0];
      if (!sheet) continue;

      for (const col of sheet.columns.filter((c) => c.included)) {
        const isJoinKey = plan.joinKey.trim() && col.deamColumnName === this.toSnakeCase(plan.joinKey.trim());
        const existing = attrMap.get(col.deamColumnName);
        if (existing) {
          const sheets = existing['sourceSheets'] as string[];
          const attrs = existing['sourceAttributes'] as string[];
          if (sheetName && !sheets.includes(sheetName)) sheets.push(sheetName);
          if (col.sourceHeader && !attrs.includes(col.sourceHeader)) attrs.push(col.sourceHeader);
          if (isJoinKey) {
            existing['businessKey'] = true;
            existing['unique'] = true;
            existing['indexed'] = true;
            existing['mandatory'] = true;
          }
        } else {
          const status = this.findStatusForColumn(sheet, col);
          const type = this.toDeamType(col.dataType);
          const attr: Record<string, unknown> = {
            name: col.deamColumnName,
            type,
            scale: type === 'numeric' ? 2 : null,
            unique: isJoinKey ? true : this.isBusinessKeyColumn(col),
            indexed: isJoinKey ? true : this.shouldIndexColumn(col),
            mandatory: isJoinKey ? true : col.mandatory,
            maxLength: this.defaultMaxLength(type),
            precision: type === 'numeric' ? 10 : null,
            businessKey: isJoinKey ? true : this.isBusinessKeyColumn(col),
            displayName: this.toDisplayName(col.sourceHeader),
            validValues: status?.allowedValues || [],
            staticString: '',
            autogenerated: false,
            autoGenerated: false,
            enableAttribute: true,
            sourceAttributes: [col.sourceHeader],
            sourceSheets: sheetName ? [sheetName] : [],
          };
          if (col.formulaMapping) attr['formulaMapping'] = col.formulaMapping;
          attrMap.set(col.deamColumnName, attr);
        }
      }
    }

    const attributes = Array.from(attrMap.values());
    const finalAttributes = this.ensureAuditAttributes(attributes);

    return {
      entity: entityName,
      deamReads: this.buildDeamReads(entityName, finalAttributes),
      primaryKey: 'uuid',
      displayName: this.toDisplayName(primaryPlan.entityName || primaryPlan.fileName.replace(/\.[^.]+$/, '')),
      businessRules: [],
      childEntities: [],
      attributesList: finalAttributes,
      primaryKeyType: 'string',
      sheetName: sourceSheets[0] || '',
      sourceFiles,
      sourceSheets,
    };
  }

  private buildDeamAttributes(sheet: DeamSheetSchemaRecommendation, sheetName: string): Record<string, unknown>[] {
    const attributes: Record<string, unknown>[] = [
      {
        name: 'id', type: 'long', scale: null, unique: false, indexed: false, mandatory: true,
        maxLength: null, precision: null, businessKey: false, displayName: 'ID',
        validValues: [], staticString: '', autogenerated: true, autoGenerated: true, enableAttribute: true,
      },
      {
        name: 'uuid', type: 'string', scale: null, unique: true, indexed: true, mandatory: true,
        maxLength: 255, precision: null, businessKey: false, displayName: 'uuid',
        validValues: [], staticString: '', autogenerated: true, autoGenerated: true, enableAttribute: true,
      },
    ];

    for (const col of sheet.columns.filter((c) => c.included)) {
      const status = this.findStatusForColumn(sheet, col);
      const type = this.toDeamType(col.dataType);
      const attribute: Record<string, unknown> = {
        name: col.deamColumnName,
        type,
        scale: type === 'numeric' ? 2 : null,
        unique: this.isBusinessKeyColumn(col),
        indexed: this.shouldIndexColumn(col),
        mandatory: col.mandatory,
        maxLength: this.defaultMaxLength(type),
        precision: type === 'numeric' ? 10 : null,
        businessKey: this.isBusinessKeyColumn(col),
        displayName: this.toDisplayName(col.sourceHeader),
        validValues: status?.allowedValues || [],
        staticString: '',
        autogenerated: false,
        autoGenerated: false,
        enableAttribute: true,
        sourceAttributes: [col.sourceHeader],
        sourceSheets: sheetName ? [sheetName] : [],
      };
      if (col.formulaMapping) attribute['formulaMapping'] = col.formulaMapping;
      if (col.role === 'status' && status) {
        attribute['statusLocation'] = this.resolveStatusLocation(sheet, status);
        attribute['statusTransitions'] = status.transitions;
      }
      attributes.push(attribute);
    }
    return this.ensureAuditAttributes(attributes);
  }

  private ensureAuditAttributes(attributes: Record<string, unknown>[]): Record<string, unknown>[] {
    for (const audit of AUDIT_ATTRIBUTES) {
      const existing = attributes.find((a) => a['name'] === audit.name);
      if (existing) {
        existing['type'] = audit.type;
        existing['mandatory'] = false;
        existing['enableAttribute'] = true;
        existing['displayName'] = existing['displayName'] || audit.displayName;
        existing['scale'] = existing['scale'] ?? null;
        existing['precision'] = existing['precision'] ?? null;
        existing['maxLength'] = audit.type === 'string' ? 255 : null;
        existing['staticString'] = existing['staticString'] || '';
        existing['autogenerated'] = false;
        existing['autoGenerated'] = false;
        continue;
      }
      attributes.push({
        name: audit.name,
        type: audit.type,
        scale: null,
        unique: false,
        indexed: false,
        mandatory: false,
        maxLength: audit.type === 'string' ? 255 : null,
        precision: null,
        businessKey: false,
        displayName: audit.displayName,
        validValues: [],
        staticString: '',
        autogenerated: false,
        autoGenerated: false,
        enableAttribute: true,
      });
    }
    return attributes;
  }

  private buildDeamReads(entityName: string, attributes: Record<string, unknown>[]): Record<string, unknown>[] {
    const enabled = attributes.filter((a) => a['enableAttribute']);
    const where = enabled.map((a, i) => ({
      operator: String(a['name']) === 'uuid' ? 'IS NOT' : '=',
      attributeName: String(a['name']),
      attributeValue: [],
      logicalOperator: i === enabled.length - 1 ? '' : 'AND',
    }));

    const dataNames = enabled
      .filter((a) => !['id', 'uuid', ...AUDIT_ATTRIBUTES.map((x) => x.name)].includes(String(a['name'])))
      .map((a) => String(a['name']));

    return [
      {
        filter: { join: {}, limit: null, where, offset: null, orderBy: {}, attributeNames: ['*'] },
        filterName: 'dynamic_filter',
        filterType: 'dynamic',
      },
      {
        filter: {
          join: {},
          limit: null,
          where: dataNames.map((n, i) => ({
            operator: '=',
            attributeName: n,
            attributeValue: [],
            logicalOperator: i === dataNames.length - 1 ? '' : 'AND',
          })),
          offset: null,
          orderBy: {},
          attributeNames: dataNames,
        },
        filterName: `${entityName}_filter`,
      },
    ];
  }


  private buildDeamBusinessRules(sheet: DeamSheetSchemaRecommendation): Record<string, unknown> | [] {
    const statusRules = sheet.statusFields
      .filter((s) => s.included)
      .flatMap((s) =>
        (s.rulePlaceholders || []).map((rule) => ({
          ...rule,
          scope: 'status',
          statusField: s.fieldName,
          statusLocation: this.resolveStatusLocation(sheet, s),
        })),
      );
    const entityName = `${this.toSnakeCase(sheet.sheetName)}_deam`;
    const globalRules = this.activeGlobalRulePlaceholders().filter((r) => !this.isFormulaCalculationRule(r));
    const allRules = [...globalRules, ...statusRules];
    const intraEntityRules = allRules.map((rule, i) => this.buildDeamRuleAttribute(rule, i));
    if (!intraEntityRules.length && !globalRules.length) return [];

    return {
      globalRuleDetails: globalRules.length
        ? [{ entityName, attributeList: globalRules.map((rule, i) => this.buildDeamRuleAttribute(rule, i)) }]
        : [],
      globalBuisnessRules: globalRules.some((r) => r.enabled),
      attributeRuleDetails: { interEntityRules: [], intraEntityRules },
      attributeBusinessRules: intraEntityRules.some((r) => {
        const filter = r['filter'] as Record<string, unknown>;
        return filter['ENABLED'] !== false;
      }),
    };
  }

  private buildDeamRuleAttribute(rule: RulePlaceholder, index: number): Record<string, unknown> {
    const metadata = rule as RulePlaceholder & {
      scope?: string;
      statusField?: string;
      statusLocation?: StatusLocation;
    };
    const source = rule.source || {};
    const sourceOutputField = typeof source['outputField'] === 'string' ? source['outputField'] : '';
    const attributeName =
      metadata.statusField || sourceOutputField || this.toSnakeCase(rule.name || `rule_${index + 1}`);
    const filter: Record<string, unknown> = {
      EXPRESSION: rule.expression || '',
      EVENT: rule.event || '',
      ENABLED: rule.enabled,
      RULE_ID: rule.ruleId || `BUS_RULE_${String(index + 1).padStart(3, '0')}`,
    };
    if (rule.fromStatus) filter['FROM_STATUS'] = rule.fromStatus;
    if (rule.toStatus) filter['TO_STATUS'] = rule.toStatus;
    if (rule.description) filter['DESCRIPTION'] = rule.description;
    if (metadata.scope) filter['SCOPE'] = metadata.scope;
    if (metadata.statusLocation) filter['STATUS_LOCATION'] = metadata.statusLocation;

    return { filter, operator: 'AND', attributeName, logicalOperator: index === 0 ? '' : 'AND' };
  }

  private isFormulaCalculationRule(rule: RulePlaceholder): boolean {
    return rule.event === 'on_calculation' && rule.source?.['type'] === 'formula';
  }

  private findStatusForColumn(
    sheet: DeamSheetSchemaRecommendation,
    column: DeamColumnRecommendation,
  ): DeamStatusFieldRecommendation | null {
    return (
      sheet.statusFields.find(
        (s) => s.included && s.sourceSheet === column.sourceSheet && s.sourceColumn === column.sourceColumn,
      ) || null
    );
  }

  private resolveStatusLocation(
    sheet: DeamSheetSchemaRecommendation,
    status: DeamStatusFieldRecommendation,
  ): StatusLocation {
    const col = this.findStatusColumn(sheet, status);
    if (!col) {
      return (
        status.statusLocation || {
          sheet: status.sourceSheet,
          column: status.sourceColumn,
          header: status.sourceHeader,
          deamColumnName: status.fieldName,
        }
      );
    }
    return {
      sheet: col.sourceSheet,
      column: col.sourceColumn,
      header: col.sourceHeader,
      deamColumnName: col.deamColumnName,
    };
  }

  private findStatusColumn(
    sheet: DeamSheetSchemaRecommendation,
    status: DeamStatusFieldRecommendation,
  ): DeamColumnRecommendation | null {
    return (
      sheet.columns.find(
        (c) =>
          c.included &&
          c.role === 'status' &&
          c.sourceSheet === status.sourceSheet &&
          c.sourceColumn === status.sourceColumn,
      ) || null
    );
  }

  private toDeamType(dataType: string): string {
    const n = dataType.toUpperCase();
    if (n === 'TEXT') return 'string';
    if (n === 'NUMERIC') return 'numeric';
    if (n === 'INTEGER') return 'integer';
    if (n === 'BOOLEAN') return 'boolean';
    if (n === 'DATE') return 'date';
    if (n === 'TIMESTAMP') return 'datetime';
    return 'string';
  }

  private defaultMaxLength(type: string): number | null {
    return type === 'string' ? 50 : null;
  }

  private shouldIndexColumn(col: DeamColumnRecommendation): boolean {
    return col.role === 'status' || this.isBusinessKeyColumn(col);
  }

  private isBusinessKeyColumn(col: DeamColumnRecommendation): boolean {
    const name = col.deamColumnName.toLowerCase();
    return name === 'id' || name.endsWith('_id') || name.includes('code');
  }

  private cloneDeamAnalysis(): DeamAnalysisResponse {
    return JSON.parse(JSON.stringify(this.deamAnalysis())) as DeamAnalysisResponse;
  }

  private findMutableDeamSheet(deam: DeamAnalysisResponse): DeamSheetSchemaRecommendation | null {
    return deam.sheetSchemas.find((s) => s.sheetName === this.activeSheetName()) || deam.sheetSchemas[0] || null;
  }

  private buildTransitions(values: string[]): StatusTransition[] {
    return values.map((v) => ({
      fromStatus: v,
      toStatuses: values.filter((c) => c !== v),
    }));
  }

  private initializeGlobalRulePlaceholders(deam: DeamAnalysisResponse): Record<string, RulePlaceholder[]> {
    return deam.sheetSchemas.reduce<Record<string, RulePlaceholder[]>>((acc, sheet) => {
      acc[sheet.sheetName] = (sheet.businessRules || []).map((rule) => ({
        ruleId: rule.ruleId,
        event: rule.event || 'on_calculation',
        fromStatus: rule.fromStatus || '',
        toStatus: rule.toStatus || '',
        name: rule.name || rule.ruleId || 'Business rule',
        description: rule.description || '',
        expression: rule.expression || '',
        enabled: rule.enabled ?? true,
        source: rule.source,
        detectionReasons: rule.detectionReasons || [],
      }));
      return acc;
    }, {});
  }

  private parseCsvValues(rawValue: string): string[] {
    return rawValue
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  }

  private toSnakeCase(value: string): string {
    return (
      value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '') || 'deam_table'
    );
  }

  private toDisplayName(value: string): string {
    return (
      value
        .replace(/[_-]+/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ') || 'DEAM Entity'
    );
  }
}
